// helpContent.ts · v12.10.25.2 (#2119 Tushar 10/30 ~9:05 AM: Linked people STAYS - the article is back, Caller / Relation added beside it)
// helpContent.ts · v12.10.25 (#2119 Help corrections: delete never deletes calls, Reports is live, no working notes) · 2026-10-30 · VoiceSpectrum AI
// helpContent.ts · v12.10.23.1 (#2101 Help follows the 12.10.19-12.10.23 screens, Riverside Family Practice screenshots, new entries; #2102 each person sees only the topics for screens their user group can open - View is enough) · 2026-10-29 · VoiceSpectrum AI
// helpContent.ts · v12.10.18 (#1863 Repeat callers wording) · 2026-10-22 · VoiceSpectrum AI
// helpContent.ts · v12.10.17 (#1723 T120 follow-up: "Files under" retired per Tushar 10/16 - the category is the intent card) · 2026-10-16 · VoiceSpectrum AI
// helpContent.ts · v12.10.16 (T113 Repeat callers help entry) · 2026-10-14 · VoiceSpectrum AI
// helpContent.ts · v12.10.11.2 (#1475 Help library; screenshots captured from UAT1, legends trimmed to the markers shown; si-sign-in marker 5 split into 5 and 6) · 2026-10-09 · VoiceSpectrum AI
// T61 #1475 - the Help library's words, one typed module. Outline: Documents\Help\HELP-LIBRARY-OUTLINE_v1.0.html
// (sections 1-7). Layout: UI-PROTO-T56 v1.25 screen "help" (LOCKED 10/9). English only.
// Every article: What it is · Where to find it · How it works · Screenshot with numbered markers · Good to know ·
// Who can do it. Written from the T2 code as it is in 12.10.11.1 - button, field and menu names exactly as on screen.
// A screenshot is a SLOT: the page shows src/pages/help/img/<article-id>.png when that file exists, otherwise a grey
// "Screenshot to follow" box with the marker legend. The capture list (which screen, which state, where each marker
// goes) is Documents\Help\HELP-SCREENSHOT-LIST_v1.4.html - built from the `capture` and `at` texts below.

/** The left-menu item a topic is listed under in the Help tab. */
export type HelpRail = 'start' | 'messages' | 'followups' | 'contacts' | 'settings';

export interface HelpMarker {
  n: number;
  label: string;
  /** Where the marker sits on the capture, in words - for whoever takes the screenshot. Not shown on the page. */
  at: string;
}

export interface HelpScreenshot {
  /** Relative to src/pages/: help/img/<article-id>.png */
  file: string;
  /** Which screen and state to capture (UAT1 or Harlem test data; black out anything that looks like a real caller). */
  capture: string;
  markers: HelpMarker[];
}

export interface HelpField {
  name: string;
  text: string;
}

export interface HelpArticle {
  id: string;
  title: string;
  /** Sub-heading in the topic list (e.g. "Organization" inside Settings). Defaults to the section's title. */
  group?: string;
  /** Overrides the section's reader (Integrations is written for the practice's IT person). */
  reader?: string;
  /** Where to find it - the menu path, exactly as on screen. */
  menuPath: string;
  /** The app route "Open … in the app" goes to. */
  appLink?: string;
  appLabel?: string;
  /** What it is. */
  what: string;
  /** How it works - short steps. */
  steps: string[];
  /** Field-by-field reference (names exactly as on screen). */
  fields?: HelpField[];
  screenshot: HelpScreenshot;
  /** Good to know / common problems. */
  goodToKnow: string[];
  /** Who can do it (user group). */
  who: string;
  keywords: string[];
  /** Also listed under these left-menu items (e.g. a common question under Messages). */
  alsoIn?: HelpRail[];
}

export interface HelpSection {
  id: string;
  title: string;
  /** Who the section is written for. */
  reader: string;
  /** The left-menu item the section belongs to. */
  rail: HelpRail;
  intro: string;
  articles: HelpArticle[];
}

export const HELP_RAILS: { key: HelpRail; label: string }[] = [
  { key: 'messages', label: 'Messages' },
  { key: 'followups', label: 'Follow-ups' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'settings', label: 'Settings' },
  { key: 'start', label: 'Getting started' },
];

const img = (id: string) => `help/img/${id}.png`;

const WHO_ANYONE = 'Everyone who can sign in to your practice.';
const WHO_MESSAGES = 'Anyone whose user group includes Messages. Looking needs View; calling back, texting, notes, assigning and Mark complete need Work.';
const WHO_CONTACTS = 'Anyone whose user group includes Contacts. Looking needs View; adding and editing need Work.';
const whoSettings = (area: string) =>
  `People whose user group can change Settings › ${area} (Work or Full). With View they can look but not save. The Owner group always can.`;

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════
export const HELP_SECTIONS: HelpSection[] = [
  // ── 1 · Getting started ─────────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'getting-started',
    title: 'Getting started',
    reader: 'front desk',
    rail: 'start',
    intro: 'What VoiceSpectrum does, your first login and how to find your way around.',
    articles: [
      {
        id: 'gs-what',
        title: 'What VoiceSpectrum does for your practice',
        menuPath: 'Messages',
        appLink: '/messages',
        appLabel: 'Messages',
        what: 'VoiceSpectrum’s AI agent answers your practice’s phone. It greets the caller, finds out why they are calling, asks for the details your practice needs and puts every call in Messages - with a short summary, the recording and the transcript.',
        steps: [
          'A caller rings your practice number. The AI agent answers.',
          'It greets them and talks in their language.',
          'It works out why they are calling and asks its questions - name, date of birth, what they need.',
          'It may put the call through to your staff, take a voicemail, or end with a thank you.',
          'The call shows up in Messages with a reference number. Your team works it from there.',
        ],
        screenshot: {
          file: img('gs-what'),
          capture: 'Messages, list on the left and one finished test call open on the right (UAT1).',
          markers: [
            { n: 1, label: 'Each call is one card in the list', at: 'click to open' },
            { n: 2, label: 'What the caller needs - the summary', at: 'top of the open message' },
            { n: 3, label: 'Recording and transcript', at: 'further down the open message' },
          ],
        },
        goodToKnow: [
          'The agent answers 24/7 while Settings › AI Agent › Phone & Activation is on.',
          'Text messages in Messages come in a later release - today it is phone calls.',
        ],
        who: WHO_ANYONE,
        keywords: ['overview', 'what is voicespectrum', 'ai agent', 'receptionist', 'how it works'],
      },
      {
        id: 'gs-first-login',
        title: 'Your first login',
        menuPath: 'The invite email or text › Set up your login',
        what: 'Your practice invites you by email (or by text if you have no email). The link opens “Set up your login”, where you add your name and choose your password.',
        steps: [
          'Open the invite and click its link.',
          'Type your First name and Last name.',
          'Mobile is optional - add it if you want sign-in codes by text.',
          'Type a password in Create a password, then again in Confirm password.',
          'Every rule under the boxes turns green when the password is good. Then save.',
          'If your practice asks for a second step, set it up next (see “The second step”).',
        ],
        fields: [
          { name: 'At least 12 characters', text: 'The practice sets the minimum; 12 is the usual. The count shows while you type.' },
          { name: 'Not the same as your email or name', text: 'Your name or email cannot be inside the password.' },
          { name: 'Not common, easy to guess or the practice’s name or short name', text: 'Passwords like “Password1234” or the practice’s name are refused.' },
          { name: 'Not one of your last 5 passwords', text: 'Shown when your practice blocks reused passwords.' },
        ],
        screenshot: {
          file: img('gs-first-login'),
          capture: 'The invite page “Set up your login” with a test invite, names typed, password half typed so some rules are green and some are not.',
          markers: [
            { n: 1, label: 'First name and Last name', at: 'the two name boxes at the top' },
            { n: 2, label: 'Mobile (optional - for text-message codes)', at: 'the Mobile box' },
            { n: 3, label: 'Create a password and Confirm password', at: 'the two password boxes' },
            { n: 4, label: 'The rules - each turns green when met', at: 'the rule list under the boxes' },
          ],
        },
        goodToKnow: [
          'Invites last 7 days. “This invite has expired” means your administrator needs to resend it (Settings › Security › Users › Resend invite).',
          '“Link already used” - each invite link works once. Go to the sign-in page and sign in, or ask for a new invite.',
          'A long phrase is easiest: “my blue mug is” is accepted where “mybluemug” is too short.',
        ],
        who: 'Anyone your practice invited.',
        keywords: ['invite', 'first time', 'set up your login', 'create password', 'new user', 'expired invite', 'welcome'],
      },
      {
        id: 'gs-find-your-way',
        title: 'Find your way around',
        menuPath: 'The left menu',
        what: 'The left menu is the same on every screen. Your account card is at the bottom of it.',
        steps: [
          'Messages - every call the agent answered.',
          'Follow-ups - calls with a reminder that is due. The number shows how many.',
          'Contacts - the people who call you.',
          'Schedule and Billing are greyed - they are coming soon. Reports is open: Call activity.',
          'Settings - how the practice and the agent are set up.',
          'Help - opens this library in a new browser tab.',
          'Click your name at the bottom for Your account and Sign out.',
        ],
        screenshot: {
          file: img('gs-find-your-way'),
          capture: 'Any app page with the left menu open in full (not collapsed), Follow-ups showing a count.',
          markers: [
            { n: 1, label: 'Your practice name - click to go to Messages', at: 'top of the left menu' },
            { n: 2, label: 'Messages, Follow-ups, Contacts', at: 'first three menu items' },
            { n: 3, label: 'Coming soon (greyed)', at: 'Schedule / Billing' },
            { n: 4, label: 'Settings and Help', at: 'Account group' },
            { n: 5, label: 'Your account card', at: 'bottom of the left menu' },
          ],
        },
        goodToKnow: [
          'A greyed item that says “Your user group doesn’t include …” means you have no access to it. Ask your practice admin.',
          'The arrow next to the practice name makes the menu narrow (icons only). Click it again to widen it.',
          'On a phone, the menu is behind the ☰ button at the top left.',
          'A “Staging” badge at the top means test mode - calls and contacts made now are cleared when VoiceSpectrum moves you to Production.',
          'When a new version is out, a bar says “A new version of VoiceSpectrum is available.” Click Reload.',
        ],
        who: WHO_ANYONE,
        keywords: ['menu', 'navigation', 'sidebar', 'left menu', 'coming soon', 'staging', 'test mode', 'reload', 'new version'],
      },
      {
        id: 'gs-glossary',
        title: 'Words you will see',
        menuPath: 'Everywhere in the app',
        what: 'The short list of words VoiceSpectrum uses, in plain English.',
        steps: [
          'Find the word below.',
          'Each one says what it means and where you see it.',
        ],
        fields: [
          { name: 'Reference number', text: 'The call’s own number, like #41-0014. Give it to the caller - anyone can find the call with it in Messages search.' },
          { name: 'Caller type', text: 'Who is calling - Patient, Pharmacy, Referring physician… Set in Settings › Personalization.' },
          { name: 'Category', text: 'What the call is about - Appointment, Rx Refill… The colored chip on each message. A call the agent could not place is “General”.' },
          { name: 'Label', text: 'A tag your team adds by hand, like “Callback Done”.' },
          { name: 'Workflow', text: 'The script the agent follows on every call. Settings › Workflow.' },
          { name: 'Intent', text: 'One reason for calling inside a caller type - Make appointment, Refill request.' },
          { name: 'Card', text: 'One step of the workflow - the greeting, a question, a read-back.' },
          { name: 'Field / token', text: 'A piece of information the call stores, like the caller’s date of birth. In wording it is written in braces, like {organization.name}, and filled in on the call.' },
          { name: 'Handoff', text: 'Putting the caller through to a person.' },
          { name: 'Voicemail', text: 'The caller leaves a message after the greeting and the tone.' },
          { name: 'Interceptor', text: 'The part of the workflow that listens on every turn for “say that again”, “give me a person” or a question.' },
        ],
        screenshot: {
          file: img('gs-glossary'),
          capture: 'One open message in Messages showing the reference number, caller type, category chip and a label.',
          markers: [
            { n: 1, label: 'Reference number', at: 'top of the open message' },
            { n: 2, label: 'Caller type', at: 'the icon and how the call came in' },
            { n: 3, label: 'Category chip', at: 'Appointment · Schedule' },
            { n: 4, label: 'Send Text', at: 'greyed when texting isn’t available' },
          ],
        },
        goodToKnow: ['Your practice can rename some of these words - for example “subject” to “Patient” in Settings › Vocabulary › Fields.'],
        who: WHO_ANYONE,
        keywords: ['glossary', 'terms', 'definitions', 'reference number', 'caller type', 'category', 'label', 'token', 'intent', 'card', 'handoff'],
      },
    ],
  },

  // ── 2 · Signing in and your account ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'signing-in',
    title: 'Signing in and your account',
    reader: 'front desk',
    rail: 'start',
    intro: 'Signing in, codes, passwords, the second step and your own account.',
    articles: [
      {
        id: 'si-sign-in',
        title: 'Sign in with your password',
        menuPath: 'The sign-in page',
        what: 'You sign in with your email or mobile number and your password.',
        steps: [
          'Type your Email or mobile number.',
          'Type your Password.',
          'Tick Keep me signed in on your own computer only.',
          'Click Sign in.',
          'If your practice uses a second step, type that code next.',
        ],
        screenshot: {
          file: img('si-sign-in'),
          capture: 'The practice sign-in page, empty (UAT1 practice).',
          markers: [
            { n: 1, label: 'Email or mobile number', at: 'first box' },
            { n: 2, label: 'Password', at: 'second box' },
            { n: 3, label: 'Keep me signed in · Forgot password?', at: 'row under the password' },
            { n: 4, label: 'Sign in', at: 'the main button' },
            { n: 5, label: 'Text me a code', at: 'left button under “or sign in with a one-time code”' },
            { n: 6, label: 'Email me a code', at: 'right button under “or sign in with a one-time code”' },
          ],
        },
        goodToKnow: [
          'A mobile number can be typed any way - (555) 555-0100 or 5555550100.',
          'Too many wrong tries locks the account for a short time - see “Locked or turned-off account”.',
          'Trouble signing in? Use Contact support at the bottom of the page.',
        ],
        who: WHO_ANYONE,
        keywords: ['sign in', 'log in', 'login', 'password', 'keep me signed in', 'email', 'mobile'],
      },
      {
        id: 'si-code',
        title: 'Sign in with a one-time code',
        menuPath: 'The sign-in page › Text me a code / Email me a code',
        what: 'No password handy? Get a 6-digit code by text or email instead.',
        steps: [
          'Type your Email or mobile number.',
          'Click Text me a code or Email me a code.',
          'The page says: “If an account matches …, we’ve texted (or emailed) a 6-digit code. It’s valid for 10 minutes.”',
          'Type the 6 digits and click Verify.',
          'No code? Click Resend code (after 30 seconds).',
        ],
        screenshot: {
          file: img('si-code'),
          capture: '“Check your phone” after Text me a code, with a test mobile typed.',
          markers: [
            { n: 1, label: 'What you typed - check it is right', at: 'the bold email / mobile in the sentence' },
            { n: 2, label: 'The 6 code boxes', at: 'the code boxes' },
            { n: 3, label: 'Verify', at: 'the main button' },
            { n: 4, label: 'Resend code', at: 'Resend code link and its timer' },
          ],
        },
        goodToKnow: [
          '“Didn’t receive the code? Check that your email or mobile number is typed correctly.” - the page always shows what you typed, so look at it first.',
          '“Technical error while sending the code. Please try another method.” - the text or email could not be sent. Use the other button, or your password.',
          'If you signed in with a texted code, your second step has to come from somewhere else - your authenticator app or a backup code.',
        ],
        who: WHO_ANYONE,
        keywords: ['one-time code', 'text me a code', 'email me a code', 'verification code', '6-digit', 'resend', 'sms code', 'technical error'],
      },
      {
        id: 'si-forgot',
        title: 'Forgot your password',
        menuPath: 'The sign-in page › Forgot password?',
        what: 'Reset your own password with an email link or a texted code.',
        steps: [
          'Click Forgot password?',
          'Type the email or mobile you sign in with. Click Send reset.',
          'Email: open the link we sent. It works for 30 minutes.',
          'Mobile: type the 6-digit code we texted. It works for 10 minutes.',
          'Choose a new password and confirm it. All rules must be green.',
          'Sign in with the new password.',
        ],
        fields: [
          { name: 'At least 12 characters', text: 'Your practice’s minimum.' },
          { name: 'Not the same as your email or name', text: 'Your name or email cannot be inside it.' },
          { name: 'Not common, easy to guess or the practice’s name or short name', text: 'Common passwords and the practice’s name are refused.' },
          { name: 'Not one of your last 5 passwords', text: 'Shown when your practice blocks reused passwords.' },
        ],
        screenshot: {
          file: img('si-forgot'),
          capture: '“Choose a new password” after a test reset, with a weak password typed so one rule shows red.',
          markers: [
            { n: 1, label: 'New password and Confirm', at: 'the two password boxes' },
            { n: 2, label: 'The strength bar', at: 'the bar under the new password' },
            { n: 3, label: 'The rules - green when met', at: 'the rule list' },
          ],
        },
        goodToKnow: [
          'After the change you are signed out on your other devices: “Password changed. You were signed out on your other devices.”',
          '“This reset link has expired or was already used.” - start again with Forgot password?',
          'If your practice turned off self-service reset, ask your administrator - they use Send password reset link in Settings › Security › Users.',
        ],
        who: WHO_ANYONE,
        keywords: ['forgot password', 'reset password', 'new password', 'reset link', 'password rules', 'expired link'],
      },
      {
        id: 'si-second-step',
        title: 'The second step (2-step verification)',
        menuPath: 'After your password › Add a second step',
        what: 'A second proof that it is you, after your password. Your practice decides if it is required.',
        steps: [
          'Choose Authenticator app (recommended) or Text message code.',
          'Authenticator app: open Google Authenticator, Microsoft Authenticator or any authenticator app, scan the QR code (or type the key), then type the 6-digit code it shows.',
          'Text message code: type your mobile. We text a 6-digit code each time.',
          'Save your 10 backup codes. Each works once. Tick I’ve saved these codes.',
          'Next time, type the code after your password.',
        ],
        screenshot: {
          file: img('si-second-step'),
          capture: '“Add a second step” with both choices visible (test user).',
          markers: [
            { n: 1, label: 'Authenticator app (Recommended)', at: 'first choice' },
            { n: 2, label: 'Text message code', at: 'second choice' },
            { n: 3, label: 'Mobile number for codes', at: 'mobile box' },
          ],
        },
        goodToKnow: [
          '“Don’t ask again on this browser for N days” - leave it ticked on your own work computer.',
          'Lost your phone? Use a backup code (Use a backup code on the code screen). None left? Your administrator uses Reset 2-step verification.',
          'Backup codes are shown once. Keep them somewhere safe, not on the same phone.',
        ],
        who: WHO_ANYONE,
        keywords: ['2-step', 'two-factor', 'mfa', '2fa', 'authenticator', 'qr code', 'backup codes', 'text message code', 'trusted browser', 'don’t ask again'],
      },
      {
        id: 'si-locked',
        title: 'Locked or turned-off account',
        menuPath: 'The sign-in page',
        what: 'What the “Account locked” and “Your access is turned off” screens mean.',
        steps: [
          'Account locked: too many wrong passwords. Wait for the timer, or click Reset your password instead.',
          'Your access is turned off: your practice disabled the account. Call your office administrator.',
          'Set a new password to continue: your account was off for a long time. Choose a new password and confirm your phone.',
        ],
        screenshot: {
          file: img('si-locked'),
          capture: 'The “Account locked” screen for a test user, timer running.',
          markers: [
            { n: 1, label: 'The time left', at: 'the countdown in the sentence' },
            { n: 2, label: 'Reset your password instead', at: 'the button' },
          ],
        },
        goodToKnow: [
          'An administrator can unlock you sooner: Settings › Security › Users › Unlock now.',
          'Locking only stops sign-in. Nothing in your account changes.',
        ],
        who: 'Anyone. Unlocking and turning an account back on is for people whose group can manage users.',
        keywords: ['locked', 'account locked', 'disabled', 'turned off', 'too many attempts', 'unlock'],
      },
      {
        id: 'si-staying',
        title: 'Staying signed in and signing out',
        menuPath: 'Your account card › Sign out',
        what: 'For safety you are signed out when you are idle, and after the longest session your practice allows.',
        steps: [
          '“Still there?” appears before an idle sign-out. Click Stay signed in.',
          'To sign out, click your name at the bottom of the left menu, then Sign out.',
          'When you are signed out, the sign-in page says why - for example “Signed out after 30 minutes of inactivity.”',
        ],
        screenshot: {
          file: img('si-staying'),
          capture: 'The “Still there?” warning over Messages (set a short idle time on a test practice).',
          markers: [
            { n: 1, label: 'Seconds left', at: 'the countdown' },
            { n: 2, label: 'Stay signed in', at: 'the button' },
          ],
        },
        goodToKnow: [
          'The times are set in Settings › Security › Platform (Idle timeout, Longest session).',
          'An administrator can sign you out everywhere (Users › Sign out everywhere). Changing your password signs out your other devices.',
        ],
        who: WHO_ANYONE,
        keywords: ['sign out', 'log out', 'logout', 'idle', 'timeout', 'still there', 'stay signed in', 'session'],
      },
      {
        id: 'si-your-account',
        title: 'Your account',
        menuPath: 'Your account card › Your account',
        what: 'Your own profile, password, 2-step methods and recent sign-ins.',
        steps: [
          'Click your name at the bottom of the left menu, then Your account.',
          'Profile: your photo, First name, Last name, Job title and Mobile.',
          'Change password: type the Current password, the New password and Confirm new password.',
          'Sign-in and 2-step verification: set up the authenticator app or a text message code; Generate new backup codes; Forget this browser.',
          'Recent security activity: your last 15 sign-ins and security changes.',
        ],
        screenshot: {
          file: img('si-your-account'),
          capture: 'The Your account drawer open for a test user.',
          markers: [
            { n: 2, label: 'Sign-in and 2-step verification', at: 'the methods block' },
            { n: 3, label: 'Change password', at: 'the button' },
            { n: 4, label: 'Recent security activity', at: 'the activity list' },
          ],
        },
        goodToKnow: [
          'Only an administrator can change your sign-in email (Settings › Security › Users).',
          'Something you don’t recognize in the activity? Change your password and tell your administrator.',
          'Notifications (email, text or desktop alerts) are coming in a later release.',
        ],
        who: WHO_ANYONE,
        keywords: ['your account', 'profile', 'photo', 'change password', 'backup codes', 'forget this browser', 'security activity'],
      },
      {
        id: 'si-support-visit',
        title: 'When VoiceSpectrum Support visits',
        menuPath: 'The purple banner at the top · Settings › Security › Platform › Support access',
        appLink: '/configuration/security',
        appLabel: 'Security',
        what: 'VoiceSpectrum Support can come into your workspace to help, only as far as your practice allows. A purple banner shows it on every page.',
        steps: [
          'The banner says who is in, the access level and the reason, and that everything is recorded.',
          'Configuration only = Settings only.',
          'Configuration + Read & write = also messages, contacts and recordings.',
          'Your practice can Block VoiceSpectrum Support at any time in Support access.',
        ],
        screenshot: {
          file: img('si-support-visit'),
          capture: 'Settings › Security › Platform, the Support access card, with Recent support sessions showing test entries.',
          markers: [
            { n: 1, label: 'Access - Configuration only or + Read & write', at: 'Access choice' },
            { n: 2, label: 'For how long', at: 'duration choice' },
            { n: 3, label: 'Block VoiceSpectrum Support', at: 'button' },
            { n: 4, label: 'Recent support sessions', at: 'history table' },
          ],
        },
        goodToKnow: [
          'Your audit log shows “VoiceSpectrum Support”; the support person’s own name is kept in VoiceSpectrum’s records.',
          'Changing Support access asks you to confirm it is you.',
        ],
        who: 'Everyone sees the banner. Changing Support access needs a group that can change it.',
        keywords: ['support', 'voicespectrum support', 'purple banner', 'support access', 'block support', 'configuration only', 'read & write'],
      },
      {
        id: 'si-contact-support',
        title: 'Contact support before you can sign in',
        menuPath: 'The sign-in page › Trouble signing in? › Contact support',
        what: 'Can’t get in at all? Send a note to support from the sign-in page.',
        steps: [
          'Click Contact support under “Trouble signing in?”.',
          'Fill in Your name, Email or mobile, and What’s happening.',
          'Click Send to support.',
          'Keep the Reference you get back.',
        ],
        screenshot: {
          file: img('si-contact-support'),
          capture: 'The Contact support form filled with test values, then the “We have your request” screen.',
          markers: [
            { n: 1, label: 'Email or mobile - where support replies', at: 'contact box' },
            { n: 2, label: 'What’s happening', at: 'message box' },
            { n: 3, label: 'Send to support', at: 'the button' },
          ],
        },
        goodToKnow: ['Urgent? Call the practice directly.', 'Sent twice? The page says “We already have your request”.'],
        who: WHO_ANYONE,
        keywords: ['contact support', 'help', 'cannot sign in', 'trouble signing in', 'support request'],
      },
    ],
  },

  // ── 3 · Working your calls - Messages ──────────────────────────────────────────────────────────────────────────
  {
    id: 'messages',
    title: 'Messages',
    reader: 'front desk',
    rail: 'messages',
    intro: 'Every call the agent answers lands in Messages. Work them top to bottom.',
    articles: [
      {
        id: 'msg-list',
        title: 'Working your calls',
        menuPath: 'Messages',
        appLink: '/messages',
        appLabel: 'Messages',
        what: 'Every call the agent answers lands in Messages. Work them top to bottom.',
        steps: [
          'Phone calls / Text messages - the two tabs.',
          'To work - how many calls still need you. Me, Reminders and Urgent narrow the list in one click.',
          'Search - name, phone or reference number.',
          'Open a call - read the summary, listen, call back.',
          'Mark complete when it is done.',
        ],
        screenshot: {
          file: img('msg-list'),
          capture: 'Messages list (Riverside test calls), no message open.',
          markers: [
            { n: 1, label: 'To work', at: 'calls that still need you' },
            { n: 2, label: 'Search', at: 'name, phone, reference or what they need' },
            { n: 3, label: 'A call card', at: 'click to open' },
            { n: 4, label: 'Phone calls / Text messages', at: 'tabs' },
            { n: 5, label: 'Me · Reminders · Urgent', at: 'quick filters' },
            { n: 6, label: 'Newest / Oldest and Date', at: 'order and period' },
          ],
        },
        goodToKnow: [
          'Give the caller the reference number (#41-0014) - anyone can find the call with it.',
          'Assigned to me (N) shows only the calls given to you. Click it again to see everyone’s.',
          'A “new messages” chip appears when calls arrive while you are scrolled down - click it to jump to the newest.',
          'In an open call the bottom line holds the player, Call path, Mark unread and Mark complete. Activity, Print and Copy are under ⋮; Rows / Paragraph is on the title line.',
        ],
        who: WHO_MESSAGES,
        keywords: ['messages', 'list', 'queue', 'to work', 'calls', 'inbox', 'assigned to me', 'live queue'],
      },
      {
        id: 'msg-search',
        title: 'Find a call (search and filters)',
        menuPath: 'Messages › Search messages · Filters · Date',
        appLink: '/messages',
        appLabel: 'Messages',
        what: 'Narrow the list to the calls you are looking for.',
        steps: [
          'Type in Search messages - a name, phone number, reference number, contact number (e.g. vs-2) or words from the summary. Separate several with commas.',
          'Click the Date button to choose the period.',
          'Click Filters and pick Status, Priority, Caller, Language, Category, Label, Due for follow-up or Assigned to. Click Show results.',
          'Each filter shows as a chip above the list. Click × on a chip to remove it, or Clear all.',
        ],
        fields: [
          { name: 'Status', text: 'Pending or Completed.' },
          { name: 'Priority', text: 'Normal or Urgent (flagged).' },
          { name: 'Caller', text: 'The caller type - Patient, Pharmacy…' },
          { name: 'Category', text: 'What the call was about.' },
          { name: 'Label', text: 'Tags your team added.' },
          { name: 'Opened · Notes · Audit', text: 'Read or unread, with or without notes, marked for audit.' },
          { name: 'Type · Outcome · Language', text: 'Calls, Missed or Voicemails; Success, Watch or Failure; English or Spanish.' },
          { name: 'Assigned to', text: 'One or more people, or Unassigned.' },
        ],
        screenshot: {
          file: img('msg-search'),
          capture: 'Messages with the Filter messages panel open, two picks on.',
          markers: [
            { n: 1, label: 'Search', at: 'name, phone, reference, contact number or words' },
            { n: 2, label: 'Date', at: 'choose the period' },
            { n: 3, label: 'Filters', at: 'opens Filter messages' },
            { n: 4, label: 'Filter groups', at: 'Assigned to, Caller, Category, Status, Label…' },
            { n: 5, label: 'Show results / Reset', at: 'apply or clear the picks' },
            { n: 6, label: 'Chips and Clear all', at: 'each filter as a chip' },
          ],
        },
        goodToKnow: [
          'Press / to jump to search and F to open filters.',
          'Your filters stay when you refresh. Clicking Messages in the left menu resets them.',
          '“No messages match your filters” - click Clear all.',
        ],
        who: WHO_MESSAGES,
        keywords: ['search', 'filter', 'find a call', 'date', 'status', 'priority', 'category', 'label', 'chips', 'clear all', 'reference number'],
      },
      {
        id: 'msg-read',
        title: 'Read a message',
        menuPath: 'Messages › open a call',
        appLink: '/messages',
        appLabel: 'Messages',
        what: 'An open message shows what the caller needs and every detail the agent captured.',
        steps: [
          'Click a card. The message opens on the right.',
          'What the caller needs: Caller, Wants and Next step - short lines, then “… more”. EN / ES shows it in another language.',
          '“Check: …” lists details the caller did not confirm. Check them when you call back.',
          'Call data - every field the agent captured. CORRECTED, FLAGGED, “not confirmed by caller” and “not captured” explain each one.',
        ],
        screenshot: {
          file: img('msg-read'),
          capture: 'An open test message.',
          markers: [
            { n: 1, label: 'Reference number - click to copy', at: 'top bar, < > move between calls' },
            { n: 2, label: 'What the caller needs', at: 'Caller · Wants · Next step' },
            { n: 3, label: 'Read in', at: 'EN / ES - shows the summary in another language' },
            { n: 4, label: 'Call back', at: 'Ctrl+K' },
            { n: 5, label: 'Send Text', at: 'T' },
            { n: 6, label: 'Add note · Set reminder · Apply label', at: 'N · R · L' },
            { n: 7, label: 'Assign', at: 'who works it' },
            { n: 8, label: 'Status', at: 'Pending / Completed' },
          ],
        },
        goodToKnow: [
          '[ and ] move to the previous and next message. Esc closes it.',
          'Copy summary and Copy call data put the text on your clipboard.',
          'Combined (a repeat caller’s calls together): every call is translated, and Copy call record lists every call.',
        ],
        who: WHO_MESSAGES,
        keywords: ['read', 'summary', 'what the caller needs', 'read in', 'translate', 'check', 'call data', 'flagged', 'corrected', 'not confirmed'],
      },
      {
        id: 'msg-listen',
        title: 'Listen to the recording',
        menuPath: 'Messages › open a call › Recording · Transcript',
        appLink: '/messages',
        appLabel: 'Messages',
        what: 'Play the call, change the speed, download it, and read the transcript.',
        steps: [
          'Press play on the recording. Drag the bar to jump.',
          'Change the playback speed, or Download recording.',
          'Transcript: As spoken shows every line in the language it was spoken. English shows non-English lines translated.',
          'Click a line to jump the recording to it.',
          'Switch on Call path to see each step the call took, inside the transcript.',
          'Call route (next to Call path): the cards the call went through, in order. Click a card to see its lines. On a Combined message it shows the newest call.',
        ],
        screenshot: {
          file: img('msg-listen'),
          capture: 'Open test message scrolled to the recording player and transcript, Call path switched on.',
          markers: [
            { n: 1, label: 'Play / seek', at: 'player bar' },
            { n: 2, label: 'Speed and Download', at: 'player buttons on the right' },
            { n: 3, label: 'As spoken / English', at: 'transcript language switch' },
            { n: 4, label: 'Call path', at: 'Call path switch' },
          ],
        },
        goodToKnow: [
          '“No recording was saved for this call - the caller hung up before leaving a message.” - see “A call has no recording”.',
          'Recording and transcript keeping are set in Settings › Call Handling › Recording.',
        ],
        who: WHO_MESSAGES,
        keywords: ['recording', 'listen', 'audio', 'play', 'speed', 'download', 'transcript', 'as spoken', 'call path', 'call route', 'cards'],
      },
      {
        id: 'msg-callback',
        title: 'Call back · Send text',
        menuPath: 'Messages › open a call › Call back · Send Text',
        appLink: '/messages',
        appLabel: 'Messages',
        what: 'Call the caller back, or send them a one-time text.',
        steps: [
          'Click Call back (Ctrl+K). The message is set to Pending.',
          'Click Send Text (T).',
          'Pick a ready text - Appointment confirmed, We tried to reach you, Please call us back, Refill is ready, Office hours - or type your own.',
          'Choose the text language. Check the number.',
          'Send. It is sent once, not a conversation, and logged as a remark on the call.',
        ],
        screenshot: {
          file: img('msg-callback'),
          capture: 'Open test message with the Send text window open, a ready text picked.',
          markers: [
            { n: 1, label: 'Call back', at: 'Call back button at the top' },
            { n: 2, label: 'Send Text', at: 'Send Text button' },
            { n: 3, label: 'Ready texts', at: 'template list' },
            { n: 4, label: 'Text language', at: 'language picker' },
          ],
        },
        goodToKnow: [
          'A greyed button says why it is not available - for example the caller declined texts, or your group does not allow it.',
          'Appointment confirmed asks for the Appointment date before it sends.',
          'Texts go from your practice number. See “Texts aren’t arriving” if one does not reach the caller.',
        ],
        who: WHO_MESSAGES,
        keywords: ['call back', 'callback', 'send text', 'sms', 'text message', 'template', 'reply'],
      },
      {
        id: 'msg-followups',
        title: 'Follow-ups',
        menuPath: 'Follow-ups (left menu) · Messages › Filters › Due for follow-up',
        appLink: '/messages?filter=follow-up',
        appLabel: 'Follow-ups',
        what: 'Calls with a reminder that is due, still not complete. The number in the left menu shows how many.',
        steps: [
          'Click Follow-ups in the left menu. It lists calls with a reminder, grouped by when they are due: Overdue, Today, Tomorrow, the next days, Next week, Later, All.',
          'Work each call: call back, add a note.',
          'Mark complete, or set a new reminder.',
        ],
        screenshot: {
          file: img('msg-followups'),
          capture: 'Follow-ups page, Overdue picked.',
          markers: [
            { n: 1, label: 'Follow-ups', at: 'its own page in the left menu' },
            { n: 2, label: 'When it’s due', at: 'Overdue · Today · Tomorrow · next days · Next week · Later · All' },
            { n: 3, label: 'Overdue', at: 'how late the reminder is' },
            { n: 4, label: 'A due call', at: 'click to open and work it' },
          ],
        },
        goodToKnow: [
          '“No follow-ups due” - nothing is waiting.',
          'Completing a call removes it from Follow-ups.',
        ],
        who: WHO_MESSAGES,
        keywords: ['follow-ups', 'follow up', 'due', 'reminder', 'badge', 'count'],
        alsoIn: ['followups'],
      },
      {
        id: 'msg-notes',
        title: 'Notes, reminders, labels',
        menuPath: 'Messages › open a call › Add note · Set reminder · Apply label',
        appLink: '/messages',
        appLabel: 'Messages',
        what: 'Write what you did, set a follow-up date, and tag the call.',
        steps: [
          'Add note (N): pick Called back, Left voicemail, Appt scheduled, Refill approved or Spoke w/ pharmacy - or write your own in Remarks / action taken. Save note.',
          'Set reminder (R): pick 1d, 2d, 3d, 4d, 1w or 2w, or a date. Click Set.',
          'Apply label (L): tick the labels. Remove one with its ×.',
        ],
        screenshot: {
          file: img('msg-notes'),
          capture: 'Open test message with Add note open, one quick note picked.',
          markers: [
            { n: 1, label: 'Quick notes', at: 'one click fills the box' },
            { n: 2, label: 'Remarks / action taken', at: 'your own words' },
            { n: 3, label: 'Add note', at: 'N' },
            { n: 4, label: 'Set reminder', at: 'R' },
            { n: 5, label: 'Apply label', at: 'L' },
            { n: 6, label: 'Save note', at: 'or Cancel' },
          ],
        },
        goodToKnow: [
          'A call with a reminder that is due shows under Follow-ups.',
          'Labels are made in Settings › Personalization › Labels.',
        ],
        who: WHO_MESSAGES,
        keywords: ['note', 'remarks', 'reminder', 'follow up', 'label', 'tag', 'action taken'],
        alsoIn: ['followups'],
      },
      {
        id: 'msg-assign',
        title: 'Assign and complete',
        menuPath: 'Messages › open a call › Assigned · Status · Chart # · Mark complete',
        appLink: '/messages',
        appLabel: 'Messages',
        what: 'Give a call to a person, flag it urgent, add the chart number, and close it.',
        steps: [
          'Assign (A): pick a person, or Assign to me. Unassign takes it off.',
          'Urgent (U, or the flag button): flags the call urgent, or removes the flag.',
          'Add chart #: type the patient’s chart number from your EHR.',
          'Mark complete (Ctrl+Enter) when the call is handled. Mark pending reopens it.',
          'Mark unread (M) puts it back as new.',
        ],
        screenshot: {
          file: img('msg-assign'),
          capture: 'Open test message with the Assign menu open.',
          markers: [
            { n: 1, label: 'Assign', at: 'A' },
            { n: 2, label: 'Search people', at: 'by name' },
            { n: 3, label: 'Assign to me', at: 'one click' },
            { n: 4, label: 'A person and their user groups', at: 'pick to assign' },
            { n: 5, label: 'Status', at: 'Pending / Completed' },
            { n: 6, label: 'Priority flag', at: 'P - urgent on or off' },
            { n: 7, label: 'Mark unread', at: 'M' },
            { n: 8, label: 'Mark complete', at: 'Ctrl+Enter' },
          ],
        },
        goodToKnow: [
          '“Your user group doesn’t allow Mark complete.” - ask your practice admin.',
          'The Status chip (Pending / Completed) does the same as the footer button.',
        ],
        who: WHO_MESSAGES,
        keywords: ['assign', 'assigned to', 'priority', 'urgent', 'flag', 'chart', 'chart number', 'mark complete', 'complete', 'mark unread', 'pending'],
      },
      {
        id: 'msg-shortcuts',
        title: 'Keyboard shortcuts',
        menuPath: 'Messages › Keyboard shortcuts (or press ?)',
        appLink: '/messages',
        appLabel: 'Messages',
        what: 'Work the list without the mouse.',
        steps: ['Open Messages.', 'Press ? (or click Keyboard shortcuts) to see the list.'],
        fields: [
          { name: '/', text: 'Focus search' },
          { name: 'F', text: 'Open filters' },
          { name: 'Up / Down', text: 'Move card highlight' },
          { name: 'Enter', text: 'Open highlighted' },
          { name: 'Esc', text: 'Close drawer' },
          { name: '[ ]', text: 'Previous / next' },
          { name: 'Ctrl+K', text: 'Call back / reply' },
          { name: 'Ctrl+Enter', text: 'Mark complete' },
          { name: 'N', text: 'Draft note' },
          { name: 'R', text: 'Draft reminder' },
          { name: 'L', text: 'Apply label' },
          { name: 'T', text: 'Send text' },
          { name: 'A', text: 'Assign' },
          { name: 'P', text: 'Print this call' },
          { name: 'U', text: 'Urgent on / off' },
          { name: 'M', text: 'Mark unread' },
        ],
        screenshot: {
          file: img('msg-shortcuts'),
          capture: 'Messages with the Keyboard shortcuts panel open.',
          markers: [
            { n: 1, label: 'The shortcut list', at: 'press ? on Messages' },
          ],
        },
        goodToKnow: ['Shortcuts do not fire while you are typing in a box.'],
        who: WHO_MESSAGES,
        keywords: ['keyboard', 'shortcuts', 'hotkeys', 'keys'],
      },
    ],
  },

  // ── 4 · Contacts ────────────────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'contacts',
    title: 'Contacts',
    reader: 'front desk',
    rail: 'contacts',
    intro: 'The people who call you - found, added, merged and deleted.',
    articles: [
      {
        id: 'ct-find-add',
        title: 'Find or add a contact',
        menuPath: 'Contacts',
        appLink: '/contacts',
        appLabel: 'Contacts',
        what: 'Contacts fill in as calls come in. You can also add one by hand.',
        steps: [
          'Search any mix - name, phone, DOB, ZIP or ID.',
          'Click a contact to open it.',
          'Add contact: type the phone plus a first or last name (or one other detail). Save.',
        ],
        screenshot: {
          file: img('ct-find-add'),
          capture: 'Contacts list with test contacts.',
          markers: [
            { n: 1, label: 'Search', at: 'any mix - name, phone, DOB, ZIP, ID' },
            { n: 2, label: '+ Add contact', at: 'button' },
            { n: 3, label: 'Import', at: 'Import contacts from a file' },
            { n: 4, label: 'A contact', at: 'green tick = text consent' },
            { n: 5, label: 'Calls chip', at: '1 call - opens in Messages' },
          ],
        },
        goodToKnow: [
          '“You don’t have access to Contacts” - ask your practice admin.',
        ],
        who: WHO_CONTACTS,
        keywords: ['contacts', 'find contact', 'add contact', 'new contact', 'search contacts', 'patients'],
      },
      {
        id: 'ct-import',
        title: 'Import contacts from a file',
        menuPath: 'Contacts › Import',
        appLink: '/contacts',
        appLabel: 'Contacts',
        what: 'Add or update many patients at once from a spreadsheet - for example an export from your practice software.',
        steps: [
          'Click Import on the Contacts page.',
          'Download template shows the columns we read.',
          'Source: where the file comes from (e.g. your practice software). Every contact the file adds or updates shows “Import from <source>”.',
          'Drop the file or choose a file - .xlsx or .csv.',
          'Click Check the file, review step 2, then Import. Nothing is written until you press Import.',
        ],
        screenshot: {
          file: img('ct-import'),
          capture: 'Import patients, step 1.',
          markers: [
            { n: 1, label: 'Import patients', at: 'Step 1 of 2 - source, file and columns' },
            { n: 2, label: 'Download template', at: 'the columns we read' },
            { n: 3, label: 'Source', at: 'shown on every contact the file adds or updates' },
            { n: 4, label: 'The file', at: '.xlsx or .csv - nothing is written until Import' },
            { n: 5, label: 'Check the file', at: 'then step 2' },
          ],
        },
        goodToKnow: ['A file can update contacts you already have as well as add new ones.'],
        who: WHO_CONTACTS,
        keywords: ['import', 'upload', 'spreadsheet', 'csv', 'xlsx', 'excel', 'bulk', 'template', 'patients'],
      },
      {
        id: 'ct-page',
        title: 'The contact page',
        menuPath: 'Contacts › open a contact',
        appLink: '/contacts',
        appLabel: 'Contacts',
        what: 'Everything about one person: details, text consent, custom fields, calls and history.',
        steps: [
          'Details: name, caller type and preferred language (set by the latest call), up to four phones, the caller and relation, email, date of birth, ZIP code, address, System ID, External ID and Source. Click Edit to change them.',
          'Text consent: Consent available, Consent declined or No consent.',
          'Custom: fields your practice added in Settings › Vocabulary › Fields.',
          'Linked people: family members on the same phone or family linked to this person.',
          'Caller and Relation (Edit details): who calls for this person - a parent, a caregiver.',
          'Calls: every call from this person. Click one to open it in Messages.',
          'History: who changed what and when.',
          'Send text sends a one-time text. Call is coming later.',
        ],
        screenshot: {
          file: img('ct-page'),
          capture: 'One test contact open in the drawer.',
          markers: [
            { n: 1, label: 'System ID - click to copy', at: 'top bar, < > steps through contacts' },
            { n: 2, label: 'Text consent chip', at: 'Consent available' },
            { n: 3, label: 'Call · Send text · ⋮', at: 'action row' },
            { n: 4, label: 'Edit', at: 'details' },
            { n: 5, label: 'Caller type / Preferred language', at: 'set by the latest call' },
          ],
        },
        goodToKnow: [
          'System ID is set by VoiceSpectrum and can’t be changed.',
          'Record text consent: pick Consent given or Declined, add a note, Record consent. It is logged with today’s date and your name.',
          'Not recorded? The AI asks on the next call if a text is offered.',
        ],
        who: WHO_CONTACTS,
        keywords: ['contact page', 'details', 'consent', 'text consent', 'history', 'calls', 'system id', 'external id', 'edit contact'],
      },
      {
        id: 'ct-linked',
        title: 'Linked people',
        menuPath: 'Contacts › open a contact › Linked people › Link person',
        appLink: '/contacts',
        appLabel: 'Contacts',
        what: 'Connect people who belong together - a parent and child, a caregiver.',
        steps: [
          'Open the contact. In Linked people, click Link person.',
          'Pick the relation - “<name> is Parent of …”.',
          'Search the other contact by name, phone or DOB, pick them, and click Link. It shows on both people.',
          'To remove it: ⋮ › Unlink.',
        ],
        screenshot: {
          file: img('ct-linked'),
          capture: 'A test contact with the Link person search open.',
          markers: [
            { n: 1, label: 'Linked people', at: 'section on the contact' },
            { n: 2, label: 'Relation', at: '<name> is Parent / Child … of' },
            { n: 3, label: 'Search a contact', at: 'name, phone or DOB' },
            { n: 4, label: 'Link', at: 'saves the link on both people' },
            { n: 5, label: 'Record consent', at: 'text consent' },
            { n: 6, label: 'Calls', at: 'every call from this person' },
          ],
        },
        goodToKnow: ['Each person who calls is also their own contact - search them by name or phone.'],
        who: WHO_CONTACTS,
        keywords: ['family', 'caregiver', 'parent', 'relation', 'caller', 'calls for', 'guardian', 'linked people'],
      },
      {
        id: 'ct-merge',
        title: 'Merge two contacts',
        menuPath: 'Contacts › open a contact › ⋮ › Merge into…',
        appLink: '/contacts',
        appLabel: 'Contacts',
        what: 'The same person twice? Merge the extra one into the one that stays.',
        steps: [
          'Open the extra contact.',
          'Click ⋮ › Merge into…',
          'Search the contact that stays - name, phone, DOB or ID. Pick it.',
          'Click Merge into <System ID>.',
        ],
        screenshot: {
          file: img('ct-merge'),
          capture: 'The Merge into… panel on a test contact, a target picked.',
          markers: [
            { n: 1, label: 'Merge … into', at: '⋮ › Merge into…' },
            { n: 2, label: 'Which contact stays', at: 'its details win, empty ones filled' },
            { n: 3, label: 'Can’t be undone yet', at: 'warning' },
            { n: 4, label: 'Back', at: 'pick another' },
            { n: 5, label: 'Merge into <System ID>', at: 'red button' },
          ],
        },
        goodToKnow: ['A merge can’t be undone yet. Check both contacts first.'],
        who: WHO_CONTACTS,
        keywords: ['merge', 'duplicate', 'same person', 'combine contacts'],
      },
      {
        id: 'ct-delete',
        title: 'Delete a contact',
        menuPath: 'Contacts › open a contact › ⋮ › Delete contact…',
        appLink: '/contacts',
        appLabel: 'Contacts',
        what: 'Remove a contact from Contacts. Its calls are never deleted - they stay in Messages.',
        steps: [
          'Open the contact. Click ⋮ › Delete contact…',
          'Click Delete to confirm, or Cancel.',
        ],
        screenshot: {
          file: img('ct-delete'),
          capture: 'The delete panel on a test contact.',
          markers: [
            { n: 1, label: 'Delete <name> (System ID)?', at: '⋮ › Delete contact…' },
            { n: 2, label: 'Restore window', at: 'Support can restore for 30 days' },
            { n: 3, label: 'Delete', at: 'red button' },
            { n: 4, label: 'Cancel', at: 'keeps the contact' },
          ],
        },
        goodToKnow: [
          'VoiceSpectrum Support can restore a deleted contact for 30 days. After that it is removed for good.',
          '“You can’t delete contacts - it needs Contacts › Delete.” Ask your practice admin.',
        ],
        who: 'People whose user group has Contacts › Delete (Full, or Custom with Delete ticked).',
        keywords: ['delete contact', 'remove contact', 'restore', '30 days'],
      },
    ],
  },

  // ── 5 · Settings ────────────────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'settings',
    title: 'Settings',
    reader: 'practice owner / admin',
    rail: 'settings',
    intro: 'How the practice and the agent are set up - one article per area card.',
    articles: [
      {
        id: 'set-home',
        title: 'Settings home and saving',
        group: 'Settings',
        menuPath: 'Settings',
        appLink: '/settings',
        appLabel: 'Settings',
        what: 'Settings is a grid of area cards. Each area has sections (cards) you change and save.',
        steps: [
          'Click an area card - Organization, AI Agent, Call Handling, Reliability, Workflow, Knowledge Base, Security, Communication, Personalization, Vocabulary, Integrations.',
          'Search settings (Ctrl K) finds a field by name, like “speech timeout”.',
          'Change a section. It turns amber and shows Unsaved.',
          'Click the section’s save button, or Discard. The bar at the bottom saves or discards everything.',
          'Leaving with unsaved changes asks “Discard your changes?”.',
        ],
        screenshot: {
          file: img('set-home'),
          capture: 'One area (Security › Platform) with an unsaved change, the amber section and the bottom bar.',
          markers: [
            { n: 1, label: 'Search settings (Ctrl K)', at: 'search box at the top' },
            { n: 2, label: 'An area - click to open it', at: 'the area cards' },
            { n: 3, label: 'Discard draft / Save all changes', at: 'top right' },
          ],
        },
        goodToKnow: ['You only see the areas your user group can open.'],
        who: whoSettings('the area you are changing'),
        keywords: ['settings', 'save', 'discard', 'unsaved', 'search settings', 'ctrl k'],
      },
      // Organization
      {
        id: 'set-org-identity',
        title: 'Identity',
        group: 'Organization',
        menuPath: 'Settings › Organization › Identity',
        appLink: '/configuration/organization',
        appLabel: 'Organization',
        what: 'The practice’s name, logo, contact details and primary language.',
        steps: [
          'Business name - the legal / full name.',
          'Display name - shown in the app and said to callers.',
          'Logo - click a box to upload the Icon (256×256) and the Full logo (up to 1024×256).',
          'Fill in Website, Primary email, Primary phone, Primary contact - name and role, Industry and the address.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-org-identity'),
          capture: 'Organization › Identity of the UAT1 practice.',
          markers: [
            { n: 1, label: 'Business name / Display name', at: 'top row' },
            { n: 2, label: 'Logo boxes - Icon and Full logo', at: 'click a box to upload' },
            { n: 3, label: 'Primary language', at: 'right of Address' },
          ],
        },
        goodToKnow: [
          'These fields are tokens the agent can say, like {organization.name}.',
          'Primary language is the first language turned on in AI Agent › Languages & Voices.',
        ],
        who: whoSettings('Organization'),
        keywords: ['identity', 'practice name', 'display name', 'business name', 'logo', 'address', 'phone', 'email', 'website', 'industry'],
      },
      {
        id: 'set-org-hours',
        title: 'Hours & locale',
        group: 'Organization',
        menuPath: 'Settings › Organization › Hours & locale',
        appLink: '/configuration/organization',
        appLabel: 'Organization',
        what: 'Your timezone and weekly opening hours. The agent uses them to know when you are open.',
        steps: [
          'Pick the Timezone.',
          'Switch each day on or off. Set Open and Close.',
          'Add a break (lunch) with From and to times.',
          'Copy to other days copies one day’s hours.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-org-hours'),
          capture: 'Hours & locale with a break on one day.',
          markers: [
            { n: 1, label: 'Timezone', at: 'box at the top' },
            { n: 2, label: 'Day switch Open / Closed', at: 'each day' },
            { n: 3, label: 'Open / Close', at: 'times' },
            { n: 4, label: 'Add a break · copy icon', at: 'copy to other days' },
          ],
        },
        goodToKnow: [
          'Calls outside these hours use your after-hours handling (Workflow › Hours check).',
          'Quick Answers can say these hours with {organization.hours}.',
        ],
        who: whoSettings('Organization'),
        keywords: ['hours', 'business hours', 'opening hours', 'timezone', 'break', 'lunch', 'weekly schedule', 'after hours'],
      },
      {
        id: 'set-org-holidays',
        title: 'Holiday dates',
        group: 'Organization',
        menuPath: 'Settings › Organization › Holiday dates',
        appLink: '/configuration/organization',
        appLabel: 'Organization',
        what: 'Days you are closed. On holidays, calls are handled using your after-hours behavior.',
        steps: [
          'Public holidays load for your region. Tick the ones you close for.',
          'A new practice starts with six ticked: New Year’s Day, Memorial Day, Independence Day, Labor Day, Thanksgiving and Christmas.',
          'Add custom date: type a Name and a Date (e.g. Founders Day). Add holiday.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-org-holidays'),
          capture: 'Holiday dates with the six default tiles ticked and one custom date.',
          markers: [
            { n: 1, label: 'Public holiday tiles - tick the ones you observe', at: 'holiday tiles' },
            { n: 2, label: 'Add custom date', at: 'last tile' },
            { n: 3, label: 'Year tabs', at: '2026 / 2027' },
          ],
        },
        goodToKnow: ['Quick Answers can list these with {organization.holidays}.'],
        who: whoSettings('Organization'),
        keywords: ['holidays', 'closed', 'closure', 'custom holiday', 'christmas', 'thanksgiving'],
      },
      // AI Agent
      {
        id: 'set-ai-phone',
        title: 'Phone & Activation',
        group: 'AI Agent',
        menuPath: 'Settings › AI Agent › Phone & Activation',
        appLink: '/configuration/assistant',
        appLabel: 'AI Agent',
        what: 'Your practice number and whether the AI Agent answers it.',
        steps: [
          'Check the status - “Your number is live and ready.”',
          'AI Agent enabled: on, the agent answers 24/7.',
          'When the AI Agent is off, choose Route to handoff (your phone line / platform users) or Route to voicemail.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-ai-phone'),
          capture: 'Phone & Activation of the UAT1 practice, agent on.',
          markers: [
            { n: 1, label: 'Your number and its status', at: 'green box' },
            { n: 2, label: 'AI Agent enabled', at: 'switch' },
          ],
        },
        goodToKnow: [
          'Calls are never dropped: with Handoff off, voicemail is turned on automatically.',
          'Turn the agent off during an outage - callers go straight to your line or voicemail.',
          'Numbers are set up by VoiceSpectrum. “No number is assigned yet” - contact support.',
        ],
        who: whoSettings('AI Agent'),
        keywords: ['phone', 'activation', 'ai agent on', 'turn off agent', 'number', 'route to voicemail', 'route to handoff'],
      },
      {
        id: 'set-ai-details',
        title: 'Agent Details (languages & voices)',
        group: 'AI Agent',
        menuPath: 'Settings › AI Agent › Agent Details',
        appLink: '/configuration/assistant',
        appLabel: 'AI Agent',
        what: 'The agent’s name and the languages and voices it speaks.',
        steps: [
          'Agent name - how the agent introduces itself (only where the greeting uses it).',
          '+ Add language - pick from the languages on your account. The first row is your primary language.',
          'Per language: choose the voice, the spoken name (e.g. English → Skylar, Spanish → Valentina), and play the Sample.',
          'Type a sentence to hear it in that voice - it is not saved.',
          'Switch line - the first words when a call changes into this language and the voice changes.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-ai-details'),
          capture: 'Agent Details with English and Spanish rows.',
          markers: [
            { n: 1, label: 'Agent name', at: 'top' },
            { n: 2, label: 'Language row - voice and name', at: 'one row per language' },
            { n: 3, label: 'Sample - play a sentence', at: 'Sample' },
            { n: 4, label: '+ Add language', at: 'button' },
            { n: 5, label: 'Switch line', at: 'what the agent says when it changes language' },
          ],
        },
        goodToKnow: [
          'The Switch line is edited in Workflow › Language detect card, on that language’s tab.',
          'Changing a voice re-records the failure messages automatically.',
        ],
        who: whoSettings('AI Agent'),
        keywords: ['agent name', 'languages', 'voices', 'voice', 'spanish', 'sample', 'switch line', 'add language'],
      },
      {
        id: 'set-ai-voicemail',
        title: 'Voicemail',
        group: 'AI Agent',
        menuPath: 'Settings › AI Agent › Voicemail',
        appLink: '/configuration/assistant',
        appLabel: 'AI Agent',
        what: 'What callers hear when a call goes to voicemail - one greeting per language.',
        steps: [
          'Turn Voicemail on.',
          'Switch language to write each language’s greeting.',
          'Use a token like the practice name so it stays right.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-ai-voicemail'),
          capture: 'Voicemail section, English tab, greeting visible.',
          markers: [
            { n: 1, label: 'Language tabs', at: 'English · EN / Spanish · ES' },
            { n: 2, label: 'Voicemail greeting', at: 'text box' },
            { n: 3, label: 'Insert token', at: 'e.g. the practice name' },
            { n: 4, label: 'Preview and Play', at: 'how it sounds' },
          ],
        },
        goodToKnow: [
          'It plays after hours, when no one answers a handoff, or when a fallback sends a call there.',
          'Voicemails show in Messages like any other call.',
        ],
        who: whoSettings('AI Agent'),
        keywords: ['voicemail', 'greeting', 'leave a message', 'tone'],
      },
      {
        id: 'set-ai-handoff',
        title: 'Handoff',
        group: 'AI Agent',
        menuPath: 'Settings › AI Agent › Handoff',
        appLink: '/configuration/assistant',
        appLabel: 'AI Agent',
        what: 'Putting a live caller through to a person - by phone line, a signed-in user, or both.',
        steps: [
          'Turn Handoff on (a new practice starts with it off).',
          'Live phone line: type the Business-hours line. After it answers, press - keys for an extension, e.g. 2,,1234#.',
          'Click Verify. VoiceSpectrum calls the line and walks the keys. It shows ✓ Verified.',
          'After hours: switch on the After-hours line OR the Outside operator line (only one), and Verify it.',
          'Save the section.',
        ],
        fields: [
          { name: 'Business-hours line', text: 'Your front desk number during open hours.' },
          { name: 'After it answers, press', text: 'Only digits, *, # and commas. Each comma waits 1 second.' },
          { name: 'After-hours line', text: 'Your own after-hours number.' },
          { name: 'Outside operator line', text: 'An answering service. Route to operator dials it at any hour once it is Verified.' },
          { name: 'Logged-in platform user', text: 'Ring signed-in users - Ring all together, Ring in order, Longest idle first; Ring timeout. (Coming.)' },
        ],
        screenshot: {
          file: img('set-ai-handoff'),
          capture: 'Handoff with a verified business-hours line and the After hours block.',
          markers: [
            { n: 1, label: 'Business-hours line', at: 'the number calls go to' },
            { n: 2, label: 'After it answers, press', at: 'keys to press' },
            { n: 3, label: 'Verify / ✓ Verified', at: 'test call' },
            { n: 4, label: 'Handoff preference', at: 'Phone line / Logged-in user' },
            { n: 5, label: 'After hours', at: 'after-hours and outside operator lines' },
          ],
        },
        goodToKnow: [
          'With nothing switched on after hours, callers go to voicemail.',
          '“Never - calls cannot reach this line yet” means it was never verified.',
        ],
        who: whoSettings('AI Agent'),
        keywords: ['handoff', 'transfer', 'live person', 'phone line', 'verify', 'extension', 'after hours', 'operator', 'answering service'],
      },
      // Call Handling
      {
        id: 'set-ch-flow',
        title: 'Conversation flow',
        group: 'Call Handling',
        menuPath: 'Settings › Call Handling › Conversation flow',
        appLink: '/configuration/call-handling',
        appLabel: 'Call Handling',
        what: 'How the agent listens, recovers from silence, and lets callers reach a person.',
        steps: [
          'Set Speech timeout and End-of-speech (how long it waits).',
          'Silent caller: how many silent turns in a row, then where the call goes - Nothing given yet (e.g. End call) or Something given (e.g. Leave a Message).',
          'Turn on escape phrases and list the Operator keywords.',
          'Keypad menus have their own section - Call Handling › Keypad menus.',
          'Save the section.',
        ],
        fields: [
          { name: 'Speech timeout', text: 'How long the agent waits in silence after a question. A capture step always waits at least 9 seconds; “say that again” at least 5.' },
          { name: 'Silent caller', text: 'Silent turns in a row before the agent gives up, then where the call goes: Nothing given yet, or Something given (the caller already said something useful).' },
          { name: 'End-of-speech', text: 'How long it waits after the caller stops talking. Auto is best; 3-4 sec if callers spell things.' },
          { name: 'Spelling pause', text: 'Extra wait between letters when a caller spells.' },
          { name: 'Caller input cleanup', text: 'Raw, Fast (recommended) or AI assist - tidies names, dates and phones.' },
          { name: 'Operator keyword detection / action / keywords', text: 'Listens for “operator, human, representative…” and hands off or sends to voicemail.' },
          { name: 'Very-low-confidence action', text: 'What to do when the agent could not hear the caller clearly.' },
          { name: 'Advanced - listening wait by kind of answer', text: 'Longer listening for sentences and free-text answers, per language.' },
        ],
        screenshot: {
          file: img('set-ch-flow'),
          capture: 'Conversation flow section, defaults.',
          markers: [
            { n: 1, label: 'Speech timeout', at: 'first field' },
            { n: 2, label: 'End-of-speech · Spelling pause · Caller input cleanup', at: 'second row' },
            { n: 3, label: 'Silent caller', at: 'section' },
            { n: 4, label: 'Nothing given yet / Something given', at: 'where a silent call goes' },
            { n: 5, label: 'Smart fallbacks', at: 'section' },
          ],
        },
        goodToKnow: ['Each field’s “?” explains it with an example. The defaults work for most practices.'],
        who: whoSettings('Call Handling'),
        keywords: ['speech timeout', 'silence', 'end of speech', 'spelling', 'operator keywords', 'escape phrase', 'silent caller', 'cleanup', 'listening wait', 'smart fallbacks'],
      },
      {
        id: 'set-ch-recording',
        title: 'Recording',
        group: 'Call Handling',
        menuPath: 'Settings › Call Handling › Recording',
        appLink: '/configuration/call-handling',
        appLabel: 'Call Handling',
        what: 'Keep call audio and transcripts, decide how long, and mask health information.',
        steps: [
          'Record call audio - on for a new practice.',
          'Keep call transcript - the text of each call.',
          'Set Transcript retention and Recording retention.',
          'Auto-redact PHI masks names, DOB and IDs in stored transcripts.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-ch-recording'),
          capture: 'Recording section, both switches on.',
          markers: [
            { n: 1, label: 'Record call audio', at: 'the red box' },
            { n: 2, label: 'Keep call transcript', at: 'switch' },
            { n: 3, label: 'Voicemail details', at: 'Smart (AI) / Basic' },
            { n: 4, label: 'Transcript retention', at: 'days' },
            { n: 5, label: 'Recording retention', at: 'days' },
            { n: 6, label: 'Disclosure announcement', at: 'set in Workflow' },
          ],
        },
        goodToKnow: [
          'The recording disclosure (“this call may be recorded”) is the Disclosure card in Workflow.',
          'Redaction masks transcript text only, not audio. It needs Keep call transcript on.',
          'Old recordings and transcripts are removed by a scheduled clean-up after the retention time.',
        ],
        who: whoSettings('Call Handling'),
        keywords: ['recording', 'record calls', 'transcript', 'retention', 'redact', 'phi', 'hipaa', 'disclosure'],
      },
      {
        id: 'set-ch-keypad',
        title: 'Keypad menus',
        group: 'Call Handling',
        menuPath: 'Settings › Call Handling › Keypad menus',
        appLink: '/configuration/call-handling',
        appLabel: 'Call Handling',
        what: 'The say-or-press menu a caller hears when the agent could not tell what they need.',
        steps: [
          'When a menu plays: the caller’s words did not match a caller type or request, the agent asked again, and that did not work either. Then it reads the menu.',
          'Saying the answer still works while the menu plays - pressing a key is a second way in.',
          'Menu keys (every keypad menu): 0 repeats the menu, * goes back to the main menu, 9 speaks with someone (only when handoff is on). # is reserved.',
          'Set each key’s wording per language and switch keys on or off.',
          'Short menus: how many requests one menu reads out.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-ch-keypad'),
          capture: 'Keypad menus section, English tab.',
          markers: [
            { n: 1, label: 'When a menu plays', at: 'explanation' },
            { n: 2, label: 'Menu keys · every keypad menu', at: 'language tabs' },
            { n: 3, label: 'Repeat this menu', at: 'key 0' },
            { n: 4, label: 'Back to the main menu', at: 'key *' },
            { n: 5, label: 'Speak with someone', at: 'key 9, only with handoff on' },
            { n: 6, label: 'Short menus', at: 'read at most N requests' },
          ],
        },
        goodToKnow: [
          'Something else is always the last choice and can’t be deleted.',
          'The digit for each caller type and request comes from Workflow.',
        ],
        who: whoSettings('Call Handling'),
        keywords: ['keypad', 'keypad menu', 'dtmf', 'press', 'digits', 'menu', 'short menu', 'repeat menu', 'say or press'],
      },
      {
        id: 'set-ch-processing',
        title: 'Processing and hold behavior',
        group: 'Call Handling',
        menuPath: 'Settings › Call Handling › Hold Behavior · Processing Behavior',
        appLink: '/configuration/call-handling',
        appLabel: 'Call Handling',
        what: 'What the caller hears while the agent is thinking, and while being transferred.',
        steps: [
          'Pick the Behavior - silence, a spoken filler phrase, or a thinking sound (hold sound for Hold).',
          'Click a sound to preview it.',
          'Set Start delay - how long before it starts (e.g. 1500 ms).',
          'For filler phrases, + Add phrase in each language.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-ch-processing'),
          capture: 'Processing Behavior with Filler phrase chosen and phrases listed.',
          markers: [
            { n: 1, label: 'Hold Behavior', at: 'what the caller hears while being connected' },
            { n: 2, label: 'Behavior', at: 'Silence / Filler phrase / Thinking sound' },
            { n: 3, label: 'Start delay', at: 'ms' },
            { n: 4, label: 'Filler phrases', at: 'by category' },
            { n: 5, label: 'A category and its phrases', at: 'Connecting' },
          ],
        },
        goodToKnow: ['A category with no phrases falls back to General, then to silence.'],
        who: whoSettings('Call Handling'),
        keywords: ['processing', 'hold', 'filler', 'thinking sound', 'hold music', 'start delay', 'transfer sound'],
      },
      // Reliability
      {
        id: 'set-rel-provider',
        title: 'Retries, timeouts and connectivity',
        group: 'Reliability',
        menuPath: 'Settings › Reliability › Provider & Processing Reliability · Platform Connectivity',
        appLink: '/configuration/reliability',
        appLabel: 'Reliability',
        what: 'What the agent does when its own AI services are slow or fail during a call.',
        steps: [
          'Set Retry — initial delay, Retry — backoff and Retry — max attempts.',
          'Set Action timeout for the agent’s own steps.',
          'Choose the Terminal failure action - Route to voicemail, Handoff, or End with message.',
          'Set Off-script tolerance and Off-script action.',
          'Platform Connectivity: Engine API timeout, Retry max, On terminal failure.',
        ],
        screenshot: {
          file: img('set-rel-provider'),
          capture: 'Reliability, first two sections, defaults.',
          markers: [
            { n: 1, label: 'Retry settings', at: 'initial delay, backoff, max attempts' },
            { n: 2, label: 'Terminal failure action', at: 'what happens when retries run out' },
            { n: 3, label: 'Off-script action', at: 'when the caller goes off script' },
          ],
        },
        goodToKnow: [
          'Failures of your connected systems (EHR, scheduling) are set in Integrations › Failure policy, not here.',
          'Defaults work for most practices.',
        ],
        who: whoSettings('Reliability'),
        keywords: ['reliability', 'retry', 'timeout', 'backoff', 'failure', 'off-script', 'connectivity'],
      },
      {
        id: 'set-rel-failure-msg',
        title: 'Failure message',
        group: 'Reliability',
        menuPath: 'Settings › Reliability › Failure message',
        appLink: '/configuration/reliability',
        appLabel: 'Reliability',
        what: 'What a caller hears, in your agent’s voice, when the agent cannot carry on.',
        steps: [
          'Failure message - the call ends: one or two sentences that say there is a problem and the call is ending.',
          'Apology + voicemail - the caller leaves a message: an apology, then the voicemail tone.',
          'Write each language.',
          'Save. The messages are recorded in your agent’s voice when you save.',
        ],
        screenshot: {
          file: img('set-rel-failure-msg'),
          capture: 'Failure message section, English tab.',
          markers: [
            { n: 1, label: 'Failure message - the call ends', at: 'text box' },
            { n: 2, label: 'Language tabs', at: 'one message per language' },
            { n: 3, label: 'On terminal failure', at: 'Platform Connectivity' },
            { n: 4, label: 'Preview · Play', at: 'how it sounds' },
          ],
        },
        goodToKnow: [
          'They still play when the voice service is down - that is why they are recorded ahead.',
          'A cleared language falls back to the next one you have set; it never becomes silence.',
        ],
        who: whoSettings('Reliability'),
        keywords: ['failure message', 'apology', 'system error', 'outage', 'call ends'],
      },
      // Knowledge Base
      {
        id: 'set-kb-retrieval',
        title: 'Turning it on, and matching',
        group: 'Knowledge Base',
        menuPath: 'Settings › Knowledge Base › Retrieval',
        appLink: '/configuration/knowledge-base',
        appLabel: 'Knowledge Base',
        what: 'The Knowledge Base lets the agent answer callers’ questions from your answers and documents.',
        steps: [
          'Turn Knowledge Base enabled on.',
          'Confidence Score - how close a match must be. Lower scores are not used; the agent says it does not have that information.',
          'Leave Chunk size, Chunk overlap and Top-K matches at their defaults unless support asks.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-kb-retrieval'),
          capture: 'Knowledge Base › Retrieval, defaults.',
          markers: [
            { n: 1, label: 'Knowledge Base enabled', at: 'switch at the top' },
            { n: 2, label: 'Confidence Score', at: 'how sure an answer must be' },
            { n: 3, label: 'Retrieval settings', at: 'defaults work for most practices' },
          ],
        },
        goodToKnow: [
          'Off: the agent never answers from documents or Quick Answers.',
          'Questions asked in the middle of a call are handled by the Interceptor in Workflow.',
        ],
        who: whoSettings('Knowledge Base'),
        keywords: ['knowledge base', 'kb', 'confidence', 'retrieval', 'answers', 'questions'],
      },
      {
        id: 'set-kb-documents',
        title: 'Documents',
        group: 'Knowledge Base',
        menuPath: 'Settings › Knowledge Base › Documents',
        appLink: '/configuration/knowledge-base',
        appLabel: 'Knowledge Base',
        what: 'Upload PDFs, Word docs and Markdown notes the agent can search.',
        steps: [
          'Upload a file.',
          'Watch Status: Pending → Indexing… → Ready.',
          'Switch a file off to exclude it without deleting it.',
        ],
        screenshot: {
          file: img('set-kb-documents'),
          capture: 'Documents table with one Ready test document.',
          markers: [
            { n: 1, label: 'Upload', at: 'top right' },
            { n: 2, label: 'Your documents', at: 'each with an on / off switch' },
          ],
        },
        goodToKnow: ['Error status? Re-upload the file, or contact support.'],
        who: whoSettings('Knowledge Base'),
        keywords: ['documents', 'upload', 'pdf', 'word', 'index', 'reindex'],
      },
      {
        id: 'set-kb-quick',
        title: 'Quick Answers',
        group: 'Knowledge Base',
        menuPath: 'Settings › Knowledge Base › Quick Answers',
        appLink: '/configuration/knowledge-base',
        appLabel: 'Knowledge Base',
        what: 'Short answers you write. They come before documents - for facts the agent must say exactly.',
        steps: [
          '▤ Load from template - pick from 16 starters (Office hours, Holiday closures, Parking…), or Skip — start blank.',
          '+ Add Quick Answer - a title, the Answer in each language, and Keywords (comma-separated).',
          'Switch Enabled on when the wording is right.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-kb-quick'),
          capture: 'Quick Answers list with Office hours (“from your practice details”) and one starter showing “Check this before turning it on”.',
          markers: [
            { n: 1, label: 'A quick answer - title, languages, answer, keywords', at: 'card' },
            { n: 2, label: 'on · edit · delete', at: 'right of the card' },
            { n: 3, label: 'Load from template / + Add Quick Answer', at: 'buttons' },
          ],
        },
        goodToKnow: [
          'Office hours and Holiday closures are built from your practice details - {organization.hours} and {organization.holidays} - so they are always right.',
          'Starters with example facts load switched off with “Check this before turning it on”. Edit them to your practice’s facts first.',
        ],
        who: whoSettings('Knowledge Base'),
        keywords: ['quick answers', 'faq', 'starter', 'template', 'office hours', 'holidays', 'keywords', 'organization.hours'],
      },
      // Security
      {
        id: 'set-sec-platform',
        title: 'Platform - sign-in rules',
        group: 'Security',
        menuPath: 'Settings › Security › Platform',
        appLink: '/configuration/security',
        appLabel: 'Security',
        what: 'Sign-in rules and sessions for everyone in your practice.',
        steps: [
          'Sessions: Idle timeout, Longest session, Sessions per person, Remember this device, Remember for.',
          'Sign everyone out - ends every session now.',
          'Who must use a second step, Grace period, Confirm again before sensitive actions, trusted device days.',
          'Methods people can use - authenticator app, text message code, one-time sign-in codes, backup codes.',
          'Passwords: Minimum length, Characters required, Password expiry, Failed attempts before lockout, Lockout lasts, Block reused passwords, People can reset their own password.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-sec-platform'),
          capture: 'Security › Platform, scrolled to show sessions and the second-step block.',
          markers: [
            { n: 1, label: 'Idle timeout · Sessions per person', at: 'Session' },
            { n: 2, label: 'Remember this device', at: 'trusted devices' },
            { n: 3, label: 'Sign everyone out', at: 'button' },
            { n: 4, label: 'Sign-in methods - who must use a second step', at: 'next section' },
          ],
        },
        goodToKnow: [
          'Common passwords: Always refused - very common or easy-to-guess passwords and the practice’s name or short name are not accepted.',
          'Turn on the authenticator app or text code before requiring a second step, or people will be locked out.',
          'Passkeys, security keys and email codes are Future.',
        ],
        who: whoSettings('Security'),
        keywords: ['security', 'platform', 'password policy', 'idle timeout', 'session', 'lockout', '2-step required', 'mfa policy', 'sign everyone out'],
      },
      {
        id: 'set-sec-support',
        title: 'Support access',
        group: 'Security',
        menuPath: 'Settings › Security › Platform › Support access',
        appLink: '/configuration/security',
        appLabel: 'Security',
        what: 'Whether VoiceSpectrum Support can enter your workspace, how much they can do and for how long.',
        steps: [
          'Access: Configuration only (Settings) or Configuration + Read & write (also messages, contacts, recordings).',
          'For how long: pick the time.',
          'Save - you confirm it is you.',
          'Block VoiceSpectrum Support stops access. Grant access again turns it back on.',
          'Recent support sessions lists every visit: started, ended, reason and changes.',
        ],
        screenshot: {
          file: img('set-sec-support'),
          capture: 'Support access card with history rows (test).',
          markers: [
            { n: 1, label: 'Access - Configuration only or + Read & write', at: 'Access choice' },
            { n: 2, label: 'For how long', at: 'duration choice' },
            { n: 3, label: 'Block VoiceSpectrum Support', at: 'button' },
            { n: 4, label: 'Recent support sessions', at: 'history table' },
          ],
        },
        goodToKnow: ['Only the practice can change this. VoiceSpectrum Support sees it read-only.'],
        who: whoSettings('Security'),
        keywords: ['support access', 'block support', 'grant access', 'support sessions', 'audit'],
      },
      {
        id: 'set-sec-users',
        title: 'Users',
        group: 'Security',
        menuPath: 'Settings › Security › Users',
        appLink: '/configuration/security',
        appLabel: 'Security',
        what: 'Everyone who can sign in, and the groups each person is in.',
        steps: [
          'Invite user: type their Email (or Mobile if no email), pick Groups, add a Message. They set their own name and password.',
          'Search by name, email or phone; filter by status or group.',
          'Click a person to open their drawer: profile, groups, sign-in methods, where they are signed in.',
          'The dot on a photo shows who is online: Active, or Idle after 10 minutes without use (newest browser counts).',
          'Use the ⋮ actions for that person (below).',
        ],
        fields: [
          { name: 'Unlock now', text: 'Lets a locked person try again right away.' },
          { name: 'Resend invite / Cancel invite', text: 'Send the invite again, or stop the link working.' },
          { name: 'Send password reset link', text: 'Emails or texts them a reset.' },
          { name: 'Reset 2-step verification', text: 'They set up a new method at next sign-in.' },
          { name: 'Sign out everywhere', text: 'Ends all their sessions.' },
          { name: 'Disable user', text: 'Signs them out and blocks sign-in. Reversible with Enable user.' },
          { name: 'Delete user', text: 'Only once disabled. Can’t be undone; history shows “(deleted)”.' },
        ],
        screenshot: {
          file: img('set-sec-users'),
          capture: 'Users list with the ⋮ menu open on a test user.',
          markers: [
            { n: 1, label: 'Invite user', at: 'top right' },
            { n: 2, label: 'Search and filters', at: 'All · Active · Disabled · groups' },
            { n: 3, label: '⋮ actions', at: 'end of each row' },
            { n: 4, label: 'User group', at: 'what the person can open' },
            { n: 5, label: 'Online now / Idle', at: 'dot on the photo, Active or Idle (10 min)' },
            { n: 6, label: 'Second step status', at: 'Authenticator app / Text message code / Needed by' },
          ],
        },
        goodToKnow: [
          'With several groups, each area gets the most generous level.',
          'You can’t disable the last person who manages users.',
        ],
        who: whoSettings('Security'),
        keywords: ['users', 'invite', 'add user', 'disable', 'delete user', 'unlock', 'reset password', 'reset 2-step', 'sign out everywhere', 'resend invite'],
      },
      {
        id: 'set-sec-groups',
        title: 'User groups',
        group: 'Security',
        menuPath: 'Settings › Security › User groups',
        appLink: '/configuration/security',
        appLabel: 'Security',
        what: 'What each group can open and change.',
        steps: [
          'Pick a group, or several to compare. Show only differences helps.',
          'Click a level on a row: None, View, Work, Full or Custom.',
          'Add group starts from the closest group. Rename, Duplicate, Reset to standard or Delete from the group menu.',
          'People in this group: + Add people… and Save groups.',
        ],
        fields: [
          { name: 'None', text: 'Can’t open it - it isn’t in their menu.' },
          { name: 'View', text: 'Read only.' },
          { name: 'Work', text: 'View + everyday actions: edit, text, call back, listen; in Settings change and save.' },
          { name: 'Full', text: 'Work + delete.' },
          { name: 'Custom', text: 'Tick exactly what is allowed.' },
        ],
        screenshot: {
          file: img('set-sec-groups'),
          capture: 'User groups grid with two groups compared.',
          markers: [
            { n: 1, label: 'Groups - click one to edit, several to compare', at: 'group chips' },
            { n: 2, label: 'Levels - None · View · Work · Full', at: 'each row' },
            { n: 3, label: 'Rename · Duplicate · Reset to standard', at: 'group actions' },
            { n: 4, label: 'Search rows', at: 'find a screen or action' },
          ],
        },
        goodToKnow: [
          'The Owner always has full access, so no one can take it away by accident. You can rename it.',
          'You can’t give a group more access than you have.',
          'Whose messages (Assigned to me / Everyone) is fixed to Everyone until it ships.',
        ],
        who: whoSettings('Security'),
        keywords: ['user groups', 'roles', 'permissions', 'access', 'levels', 'owner', 'none view work full custom'],
      },
      // Communication
      {
        id: 'set-comm-email',
        title: 'Email (SMTP)',
        group: 'Communication',
        menuPath: 'Settings › Communication › Email (SMTP)',
        appLink: '/configuration/communication',
        appLabel: 'Communication',
        what: 'Send the practice’s email from your own mail server. Without it, VoiceSpectrum’s default is used.',
        steps: [
          'Fill in SMTP host, Port, Username, Password, From address, From name, Reply-to and Encryption.',
          'Save the section.',
          'Send a test email to an address you can check.',
        ],
        fields: [
          { name: 'SMTP host', text: 'e.g. smtp.sendgrid.net' },
          { name: 'Port', text: '587 STARTTLS, 465 SSL, 25 plain.' },
          { name: 'Password', text: 'Stored encrypted and never shown again.' },
          { name: 'From address', text: 'Must be on a sending domain your provider allows.' },
        ],
        screenshot: {
          file: img('set-comm-email'),
          capture: 'Email (SMTP) with test values, password hidden.',
          markers: [
            { n: 1, label: 'Server fields', at: 'host, port, username, password' },
            { n: 2, label: 'From address and name', at: 'what people see' },
            { n: 3, label: 'Send a test email', at: 'after you save' },
            { n: 4, label: 'Status', at: 'live on the platform default until you set your own' },
          ],
        },
        goodToKnow: ['“Your email server refused our last attempt, so VoiceSpectrum sent for you.” - check your server details.'],
        who: whoSettings('Communication'),
        keywords: ['email', 'smtp', 'mail server', 'test email', 'from address'],
      },
      {
        id: 'set-comm-sms',
        title: 'SMS testing',
        group: 'Communication',
        menuPath: 'Settings › Communication › SMS testing',
        appLink: '/configuration/communication',
        appLabel: 'Communication',
        what: 'Check that text messages from your practice number arrive.',
        steps: ['Type a mobile in Send a test text to.', 'Send it and check the phone.'],
        screenshot: {
          file: img('set-comm-sms'),
          capture: 'SMS testing section.',
          markers: [
            { n: 1, label: 'Send a test text to', at: 'number box and Send test' },
            { n: 2, label: 'Text messaging is enabled', at: 'your practice number' },
          ],
        },
        goodToKnow: [
          '“SMS is not enabled for this organization’s phone number.” - contact support.',
          'The first text to a number ends with “Reply STOP to opt out, HELP for help.” How people agree to texts: voicespectrum.ai/sms-consent.',
        ],
        who: whoSettings('Communication'),
        keywords: ['sms', 'text', 'test text', 'text messages'],
      },
      // Personalization
      {
        id: 'set-pz-callertypes',
        title: 'Caller types',
        group: 'Personalization',
        menuPath: 'Settings › Personalization › Caller types',
        appLink: '/configuration/personalization',
        appLabel: 'Personalization',
        what: 'Who is calling - Patient, Pharmacy, Referring physician. The color paints the caller’s tile in Messages.',
        steps: ['Add or Edit a caller type.', 'Set Name, Key, Color, Icon and Active.', 'Save the section.'],
        screenshot: {
          file: img('set-pz-callertypes'),
          capture: 'Caller types list with the edit drawer open.',
          markers: [
            { n: 1, label: 'A caller type', at: 'name comes from the workflow' },
            { n: 2, label: 'Color and Icon', at: 'the avatar tile in Messages' },
            { n: 3, label: 'Edit', at: 'pencil' },
            { n: 4, label: 'Allow agents to override', at: 'change a message’s caller type' },
          ],
        },
        goodToKnow: ['One type is always present for callers the agent can’t classify. It can’t be edited or removed.'],
        who: whoSettings('Personalization'),
        keywords: ['caller types', 'patient', 'pharmacy', 'color', 'icon'],
      },
      {
        id: 'set-pz-categories',
        title: 'Categories',
        group: 'Personalization',
        menuPath: 'Settings › Personalization › Categories',
        appLink: '/configuration/personalization',
        appLabel: 'Personalization',
        what: 'What a call is about - the colored chip on each message, and its filter.',
        steps: [
          'Add or Edit a category: Name, Key, Color, Icon.',
          'Used by shows which workflow intents file calls here, with Open card →.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-pz-categories'),
          capture: 'Edit category drawer for Rx Refill showing Used by.',
          markers: [
            { n: 1, label: 'A category', at: 'name and chip' },
            { n: 2, label: 'Used by - the intents that file into it', at: 'one category can serve several intents' },
            { n: 3, label: 'Color and Icon', at: 'the chip in Messages' },
            { n: 4, label: 'Edit', at: 'pencil' },
          ],
        },
        goodToKnow: [
          'A call’s category is the intent card it took in Workflow - each intent card is a category.',
          '“No caller type has this card in the workflow.” - the card was removed from every caller type.',
          'General is always present for calls the agent can’t place.',
          'One category can serve several intents. Merging categories keeps the category on old messages.',
        ],
        who: whoSettings('Personalization'),
        keywords: ['categories', 'category', 'chip', 'used by', 'files under', 'general'],
      },
      {
        id: 'set-pz-labels',
        title: 'Labels',
        group: 'Personalization',
        menuPath: 'Settings › Personalization › Labels',
        appLink: '/configuration/personalization',
        appLabel: 'Personalization',
        what: 'Tags your team puts on messages by hand, like Callback Done or Follow-up Required.',
        steps: ['Add a label: Name, Description, Color.', 'Save the section.'],
        screenshot: {
          file: img('set-pz-labels'),
          capture: 'Labels list.',
          markers: [
            { n: 1, label: 'A label', at: 'color and name' },
            { n: 2, label: 'Edit · Delete', at: 'icons on the label' },
            { n: 3, label: '+ Add label', at: 'top of the section' },
          ],
        },
        goodToKnow: ['Labels are put on by people, never by the agent.'],
        who: whoSettings('Personalization'),
        keywords: ['labels', 'tags'],
      },
      {
        id: 'set-pz-grouping',
        title: 'Repeat callers',   // v12.10.16 (T113 #1689 #1690 #1691): was Message grouping
        group: 'Personalization',
        menuPath: 'Settings › Personalization › Repeat callers',
        appLink: '/configuration/personalization',
        appLabel: 'Personalization',
        what: 'What a caller hears when the same number calls again soon after, and where each of their answers takes the call.',
        steps: [
          'Mode · Classic: off uses the behavior below; on works as before.',
          'Pick a language tab - every line is per language.',
          'Message received: the line a repeat caller hears.',
          'For each answer - Yes, Yes + details, No, Silent - pick the card the call goes to. The card name is a link to it in Workflow.',
          'Save now in the bar at the bottom (or Discard).',
        ],
        screenshot: {
          file: img('set-pz-grouping'),
          capture: 'Repeat callers section.',
          markers: [
            { n: 1, label: 'Mode - Classic', at: 'off = the new behavior below' },
            { n: 2, label: 'Language tabs', at: 'one line per language' },
            { n: 3, label: 'Message received', at: 'what a repeat caller hears' },
            { n: 4, label: 'Each answer and where it goes', at: 'Yes / Yes + details / No / Silent - the card name is a link' },
            { n: 5, label: 'Unsaved change - Discard / Save now', at: 'bottom bar' },
          ],
        },
        goodToKnow: ['How repeat calls show in Messages - one card or separate messages - is set in Workspace.'],
        who: whoSettings('Personalization'),
        keywords: ['repeat caller', 'same caller', 'called again', 'message received', 'classic'],
      },
      {
        id: 'set-pz-workspace',
        title: 'Workspace',
        group: 'Personalization',
        menuPath: 'Settings › Personalization › Workspace',
        appLink: '/configuration/personalization',
        appLabel: 'Personalization',
        what: 'How the Messages screen behaves for your practice: repeat callers, refresh and automatic status.',
        steps: [
          'When the same caller calls back: Group with the previous calls (one card, ×N times, Combined in the drawer) or Separate message.',
          'Grouping window: minutes, or Same day.',
          'Refresh the Messages list every … seconds.',
          'Automatic status: for example, mark missed calls with no details complete.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-pz-workspace'),
          capture: 'Workspace section.',
          markers: [
            { n: 1, label: 'When the same caller calls back', at: 'Group with the previous calls / Separate message' },
            { n: 2, label: 'Grouping window', at: 'minutes, or Same day' },
            { n: 3, label: 'Refresh the Messages list every', at: 'seconds' },
            { n: 4, label: 'Automatic status', at: 'e.g. missed calls with no details complete' },
          ],
        },
        goodToKnow: ['What the caller hears on a repeat call is set in Repeat callers.'],
        who: whoSettings('Personalization'),
        keywords: ['workspace', 'grouping', 'combined', 'group calls', 'refresh', 'automatic status', 'missed calls', 'same day'],
      },
      // Vocabulary
      {
        id: 'set-voc-fields',
        title: 'Fields',
        group: 'Vocabulary',
        menuPath: 'Settings › Vocabulary › Fields',
        appLink: '/configuration/vocabulary',
        appLabel: 'Vocabulary',
        what: 'The people and things a call is about, in your words. Your label shows everywhere; the term never changes, so workflows keep working.',
        steps: [
          'Rename a role under Your label - for example the subject to “Patient”, the provider to “Doctor”.',
          'Click a field to edit it: Name, Key, Type.',
          '+ Add field adds your own field to a term. + Add new vocabulary creates a term (Medication, Insurance…).',
          'Switch a field Off to hide it from screens. Calls still store it.',
          'Save the section.',
        ],
        screenshot: {
          file: img('set-voc-fields'),
          capture: 'Vocabulary › Fields with one field drawer open.',
          markers: [
            { n: 1, label: 'Term and Your label', at: 'the term never changes, your label shows everywhere' },
            { n: 2, label: 'Fields of a term', at: 'click a field to edit it' },
            { n: 3, label: '+ Add field', at: 'custom field on that term' },
            { n: 4, label: '+ Add new vocabulary', at: 'a term of your own' },
          ],
        },
        goodToKnow: ['A field’s Key never changes - workflows bind to it. Renaming its Name never rewrites what is stored.'],
        who: whoSettings('Vocabulary'),
        keywords: ['vocabulary', 'fields', 'custom field', 'rename', 'patient', 'doctor', 'token', 'off'],
      },
      {
        id: 'set-voc-lists',
        title: 'Lists',
        group: 'Vocabulary',
        menuPath: 'Settings › Vocabulary › Lists',
        appLink: '/configuration/vocabulary',
        appLabel: 'Vocabulary',
        what: 'The values a field can hold - your providers, locations, visit types.',
        steps: [
          '+ Add new list. Give it a name and what it is for. Save it.',
          '+ Add entry: Name, Description, what a Caller might say, what the AI says back, and each connected system’s id.',
          'Import… a CSV, TSV or JSON file - match its columns, see the changes, then Save.',
          'Tie the list to a field in Vocabulary › Fields.',
        ],
        screenshot: {
          file: img('set-voc-lists'),
          capture: 'A Providers list with entries and the entry drawer.',
          markers: [
            { n: 1, label: '+ Add new list', at: 'providers, locations, visit types' },
            { n: 2, label: 'Lists', at: 'tie a field to a list in Fields' },
            { n: 3, label: 'A custom term and its fields', at: 'e.g. medication' },
          ],
        },
        goodToKnow: [
          'An import never removes an entry missing from the file - calls already taken point at it.',
          'Excel files: Save as CSV first. Nothing is uploaded until you Save.',
        ],
        who: whoSettings('Vocabulary'),
        keywords: ['lists', 'providers', 'locations', 'visit types', 'import', 'csv', 'entries', 'aliases'],
      },
      // Integrations - for the practice's IT person
      {
        id: 'set-int-webhooks',
        title: 'Webhooks',
        group: 'Integrations',
        reader: 'the practice’s IT person',
        menuPath: 'Settings › Integrations › Webhooks',
        appLink: '/configuration/integrations',
        appLabel: 'Integrations',
        what: 'Outbound HTTPS POSTs of call events to your own systems - one endpoint per destination, each subscribing to its own events. Background only: delivery never blocks or delays a live caller.',
        steps: [
          'Add endpoint. Fill in Endpoint name, Webhook URL, Auth header name and Auth header value.',
          'Tick the Event subscriptions this endpoint should receive (a new endpoint has all ticked).',
          'Save, then Send test event on the endpoint card.',
          'Switch the section’s Enabled on. It needs at least one endpoint - “Add an endpoint first”.',
          'Save the section.',
        ],
        fields: [
          { name: 'Enabled', text: 'Master switch for all webhook delivery. A new practice starts Off; it can only be switched on once an endpoint exists. Turn it off to pause every endpoint at once (e.g. during a migration).' },
          { name: 'Endpoint name', text: 'Your label for the card and test toasts (e.g. Practice EHR). Not sent.' },
          { name: 'Webhook URL', text: 'https://host.domain/path. Blank keeps the endpoint “Not configured”; a valid URL reads Active when enabled.' },
          { name: 'Auth header name', text: 'Header sent with each POST. Default Authorization; or a custom header such as X-Api-Key.' },
          { name: 'Auth header value', text: 'e.g. “Bearer eyJhbGciOi…”. AES-encrypted at rest, never shown again. On edit the box reads “Saved — leave blank to keep”.' },
          { name: 'Event subscriptions', text: 'call.started (a call begins) · call.ended (a call completes) · call.handoff (handed to a human) · call.voicemail (voicemail captured) · identity.captured (caller identified) · kb.answered (AI answered from the Knowledge Base) · error (engine raised an error). The card shows “N of M events”.' },
          { name: 'Send test event', text: 'POSTs now, Content-Type application/json, with your auth header: {"event":"webhook.test","sentAt":"<ISO-8601 time>","tenant":"<practice short name>","sample":true}. Any 2xx = “Test event delivered — HTTP 200”. Otherwise “Test failed — HTTP <code>” or “Timed out after 10s”.' },
        ],
        screenshot: {
          file: img('set-int-webhooks'),
          capture: 'Webhooks section with one test endpoint (hooks.example.com) and the endpoint drawer open.',
          markers: [
            { n: 1, label: 'Endpoint name', at: 'e.g. Billing system' },
            { n: 2, label: 'Webhook URL', at: 'where events POST' },
            { n: 3, label: 'Auth header name / value', at: 'stored securely, never shown again' },
            { n: 4, label: 'Event subscriptions', at: 'only ticked events POST here' },
            { n: 5, label: 'Save endpoint', at: 'bottom of the drawer' },
          ],
        },
        goodToKnow: [
          'Live call events are delivered once integrations are active for your practice (coming); Send test event works today. The live payload is documented when that ships.',
          'Retries and what happens after the last failed try are in Failure policy.',
          'Credentials are write-only: leave the value blank to keep it, type a new one to replace it.',
        ],
        who: whoSettings('Integrations'),
        keywords: ['webhooks', 'endpoint', 'events', 'call.started', 'call.ended', 'payload', 'post', 'auth header', 'test event', 'it'],
      },
      {
        id: 'set-int-connected',
        title: 'Connected systems',
        group: 'Integrations',
        reader: 'the practice’s IT person',
        menuPath: 'Settings › Integrations › Connected systems',
        appLink: '/configuration/integrations',
        appLabel: 'Integrations',
        what: 'External systems the agent can call during a conversation - check availability, book, look up a patient, fetch a chart number. You define each system, its auth, and the actions the agent may run. A workflow Connect card picks the system and action.',
        steps: [
          'Add integration.',
          'Fill in Connection name, Type and Auth method, then the connectivity fields for that type.',
          'For OAuth 2.0, fill in the token block and test it.',
          'Add actions (next article).',
          'Save the drawer. Check connection on the card tests what is saved.',
        ],
        fields: [
          { name: 'Connection name', text: 'Your label, e.g. Scheduling API. Not sent.' },
          { name: 'Type', text: 'REST API or FHIR - request/response APIs with per-call actions. HL7 v2 and SFTP - message/file feeds with no actions; stored now, used once their transport ships.' },
          { name: 'Auth method', text: 'Bearer token · API key header · Basic auth · OAuth 2.0 client credentials · None. (SFTP uses a password instead.)' },
          { name: 'Base URL', text: 'Root of the API, e.g. https://api.example.com. Each action path is appended. Blank keeps the connection “not configured”.' },
          { name: 'FHIR version', text: 'R4, R4B, R5 or STU3 (only for servers older than R4).' },
          { name: 'MLLP endpoint · Message types', text: 'HL7: host:port (e.g. ehr.example.com:2575) and families such as ADT, SIU.' },
          { name: 'Host · Port · Username · Remote path', text: 'SFTP: port 22 by default; the folder files are picked up from (e.g. /inbound).' },
          { name: 'Token URL', text: 'OAuth 2.0 token endpoint (full URL, can be another host). We POST grant_type, client_id, client_secret and scope, form-encoded.' },
          { name: 'grant_type · client_id · scope', text: 'client_credentials for server-to-server. scope is optional and space-separated; blank is not sent.' },
          { name: 'Secret', text: 'Token, API key, client secret or SFTP password. AES-encrypted, never shown again; blank on edit keeps it.' },
        ],
        screenshot: {
          file: img('set-int-connected'),
          capture: 'Connected systems with the Harlem test REST connection card and its drawer open (secrets hidden).',
          markers: [
            { n: 1, label: 'Connection name', at: 'e.g. Practice EHR' },
            { n: 2, label: 'Type and Auth method', at: 'REST API, Bearer token…' },
            { n: 3, label: 'Base URL', at: 'the system’s address' },
            { n: 4, label: 'Credential', at: 'stored securely, never shown again' },
            { n: 5, label: '+ Add action', at: 'the calls the AI can make' },
          ],
        },
        goodToKnow: [
          'A connection reads Connected when its required fields are present - there is no live probe. Use the tests.',
          'Every test runs what is on screen. Nothing is saved until you save the drawer.',
        ],
        who: whoSettings('Integrations'),
        keywords: ['connected systems', 'integration', 'rest', 'fhir', 'hl7', 'sftp', 'oauth', 'bearer', 'api key', 'basic auth', 'base url', 'token url', 'ehr', 'it'],
      },
      {
        id: 'set-int-actions',
        title: 'Actions, test and response',
        group: 'Integrations',
        reader: 'the practice’s IT person',
        menuPath: 'Settings › Integrations › Connected systems › (drawer) › Actions',
        appLink: '/configuration/integrations',
        appLabel: 'Integrations',
        what: 'Each call the agent may make on a REST or FHIR connection: the request it sends, the test, and what the answer means.',
        steps: [
          'Add action. Type the Action name (e.g. Book appointment) and the description the AI reads.',
          'Pick the method (GET, POST, PUT, PATCH, DELETE) and the path - appended to the Base URL, or a full URL.',
          'Put captured values in the path or body with “Insert a captured value”, e.g. /appointments?member={subject.memberId}.',
          'Set Content-Type and the Request body (JSON). Sample values lists every field it sends.',
          'Run test - sends a real request (fetching the OAuth token first).',
          'Response: paste a Sample response (or Use the last test response), then set Result field, Means success, Failure values and Keep from the answer.',
          'Save the drawer.',
        ],
        fields: [
          { name: 'Sample values', text: 'Label, Field, Type, Format, Value sent - token fields fill from the call; fixed values go out as written.' },
          { name: 'Result field', text: 'Their field that says whether it worked. Blank = only the HTTP status decides - not enough for a system that answers 200 with a failure inside.' },
          { name: 'Means success', text: 'The value that means it worked, compared ignoring case.' },
          { name: 'Failure values', text: 'Optional, e.g. false, 0, error.' },
          { name: 'Keep from the answer', text: 'Their field → Our token. Kept on the call after a success, so a Confirm card can read back their confirmation number.' },
        ],
        screenshot: {
          file: img('set-int-actions'),
          capture: 'One action open on the Harlem test connection: body, Sample values, Run test result and Response block.',
          markers: [
            { n: 1, label: 'Method and path', at: 'action header' },
            { n: 2, label: 'Request body', at: 'body box' },
            { n: 3, label: 'Run test', at: 'button' },
            { n: 4, label: 'Result field / Means success', at: 'response block' },
            { n: 5, label: 'Keep from the answer', at: 'mapping rows' },
          ],
        },
        goodToKnow: [
          'A response that is not a success never becomes a confirmation - the caller goes down the Connect card’s failure route.',
          '“The request body is not valid JSON” - fix it before saving; Tidy formats a pasted sample.',
        ],
        who: whoSettings('Integrations'),
        keywords: ['actions', 'request body', 'run test', 'response', 'result field', 'success', 'mapping', 'keep from the answer', 'json', 'payload', 'it'],
      },
      {
        id: 'set-int-failure',
        title: 'Failure policy',
        group: 'Integrations',
        reader: 'the practice’s IT person',
        menuPath: 'Settings › Integrations › Failure policy',
        appLink: '/configuration/integrations',
        appLabel: 'Integrations',
        what: 'One policy for integration failures - background webhook delivery, and connected-system calls during a live call.',
        steps: [
          'Webhook delivery failure: pick the retry schedule, what happens on final failure, and the alert email.',
          'Connected-system failure: set Action timeout, Retry attempts, Caller fallback and the line the agent says.',
          'Save the section.',
        ],
        fields: [
          { name: 'Retry schedule (webhooks)', text: '3 retries · exponential backoff (30s / 2m / 10m) · 5 retries (30s / 2m / 10m / 30m / 2h) · 1 retry after 60s · None. Retried on 5xx, timeout or unreachable.' },
          { name: 'On final failure (webhooks)', text: 'Queue for manual replay · Drop and alert admin · Drop silently.' },
          { name: 'Alert recipient', text: 'Email told when a webhook finally fails, e.g. ops@yourpractice.com.' },
          { name: 'Action timeout (connected systems)', text: 'How long we wait for their endpoint, e.g. 6,000 ms. Your own AI providers keep a separate timeout in Reliability.' },
          { name: 'Retry attempts', text: '1 retry, 2 retries, or No retry - fall back immediately.' },
          { name: 'Caller fallback', text: 'Take a message & log a follow-up · Hand off to staff · Continue without the lookup · Route to voicemail.' },
          { name: 'Fallback line', text: 'What the agent says first, e.g. “I’m having trouble reaching our scheduling system right now…”.' },
        ],
        screenshot: {
          file: img('set-int-failure'),
          capture: 'Failure policy section, both halves.',
          markers: [
            { n: 1, label: 'Retry policy', at: 'webhook retries' },
            { n: 2, label: 'On final failure', at: 'queue for manual replay' },
            { n: 3, label: 'Alert recipient', at: 'who is emailed' },
            { n: 4, label: 'Action timeout', at: 'how long the AI waits on a connected system' },
            { n: 5, label: 'Retry attempts', at: 'tries before falling back' },
            { n: 6, label: 'Caller fallback', at: 'what the caller gets instead' },
            { n: 7, label: 'Worst case a caller is held', at: 'computed from 4 and 5' },
          ],
        },
        goodToKnow: [
          'The screen shows how long a caller can be held in total (timeout × tries), capped at 60 seconds.',
          'A Connect card can set its own failure route; left blank, the Caller fallback here is used.',
        ],
        who: whoSettings('Integrations'),
        keywords: ['failure policy', 'retry', 'timeout', 'fallback', 'replay', 'alert', 'webhook failure', 'connected system failure', 'it'],
      },
    ],
  },

  // ── 6 · Your call workflow ──────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'workflow',
    title: 'Your call workflow',
    reader: 'practice owner / admin',
    rail: 'settings',
    intro: 'Settings › Workflow - the script the agent follows on every call.',
    articles: [
      {
        id: 'wf-overview',
        title: 'How a call flows',
        group: 'Workflow',
        menuPath: 'Settings › Workflow',
        appLink: '/configuration/workflow',
        appLabel: 'Workflow',
        what: 'The workflow is a row of cards. Every call runs them in order.',
        steps: [
          'Before the caller is classified: Inbound call → Hours check → Disclosure → ID check → Greeting → Language detect → Classify.',
          'Classify picks a caller type (a workflow) and an intent inside it.',
          'The intent’s cards run in order - Identity, the request, Confirm, Connect, Announce, Text. Shared cards run in every intent of that caller type.',
          'Interceptor listens on every caller turn; Phrases holds every line the agent speaks.',
          'The call ends at a terminal: Transfer to staff, Leave a Message, End call, Route to operator or Thank You.',
          'Card View shows the cards; Tree View the whole outline; Tech View the technical detail.',
          'Runtime simulator (bottom of the page): type a caller phrase to see where it would go.',
        ],
        screenshot: {
          file: img('wf-overview'),
          capture: 'Workflow Card View of the UAT1 practice, pre-routing lane and one caller type visible.',
          markers: [
            { n: 1, label: 'Card View / Tree View / Tech View', at: 'tabs' },
            { n: 2, label: 'Pre-routing cards', at: 'Inbound call → Hours check → Disclosure → ID check → Greeting → Language detect → Classify' },
            { n: 3, label: 'Change template', at: 'Support only' },
            { n: 4, label: 'Preview runtime', at: 'button' },
          ],
        },
        goodToKnow: ['Click any card to open its drawer. A card no call can reach says so on the card; a DISABLED card is skipped.'],
        who: whoSettings('Workflow'),
        keywords: ['workflow', 'designer', 'cards', 'flow', 'card view', 'tree view', 'tech view', 'lanes'],
      },
      {
        id: 'wf-before',
        title: 'Before the caller is classified',
        group: 'Workflow',
        menuPath: 'Settings › Workflow › Hours check · Disclosure · ID check · Greeting · Language detect',
        appLink: '/configuration/workflow',
        appLabel: 'Workflow',
        what: 'The cards every call passes first.',
        steps: [
          'Hours check: open or closed, from Organization hours and holidays. Choose where after-hours calls go and the after-hours line.',
          'Disclosure: statements read at the start - e.g. the recording notice. Turn a block off to skip it; + Add disclosure.',
          'ID check: looks the caller up in Contacts to know who is calling.',
          'Greeting: the first thing the caller hears, per language - Regular, ID matched (the ID check confirmed who is calling) and Repeat caller. Each can be switched off.',
          'Language detect: finds the caller’s language; can announce other languages and offer a switch.',
        ],
        screenshot: {
          file: img('wf-before'),
          capture: 'Greeting card drawer, English tab.',
          markers: [
            { n: 1, label: 'Language tabs', at: 'EN / ES' },
            { n: 2, label: 'Regular greeting', at: 'every call, or ID check found no one' },
            { n: 3, label: 'Greeting text', at: 'what the caller hears first' },
            { n: 4, label: 'Insert token', at: 'e.g. {organization.name}' },
            { n: 5, label: 'ID matched greeting', at: 'after ID check confirmed the caller (Repeat caller below)' },
          ],
        },
        goodToKnow: [
          'New text uses {organization.name} - the practice’s Display name.',
          'An hours check can only fall back to voicemail.',
        ],
        who: whoSettings('Workflow'),
        keywords: ['hours check', 'after hours', 'disclosure', 'recording notice', 'greeting', 'first words', 'language detect', 'spanish'],
      },
      {
        id: 'wf-classify',
        title: 'Classify, caller types and keywords',
        group: 'Workflow',
        menuPath: 'Settings › Workflow › Classify',
        appLink: '/configuration/workflow',
        appLabel: 'Workflow',
        what: 'Classify reads the caller’s first sentence and routes to a caller type and intent, so the agent skips questions the caller already answered.',
        steps: [
          'Wait for response, Ask cap and Confidence threshold: how long the agent waits, how many times it asks, how sure it must be.',
          'If the reason can’t be worked out: the intent those calls go to.',
          'Re-ask line: what the agent says when it didn’t catch the reason. The answer is read as the caller type.',
          'When nothing matches: Offer keypad, Re-ask once, Operator or Voicemail. The keypad menu’s keys are in Call Handling › Keypad menus.',
          'Keyword library: keywords for each caller type and intent - a keyword hit routes at once.',
        ],
        screenshot: {
          file: img('wf-classify'),
          capture: 'Classify drawer, Classification section.',
          markers: [
            { n: 1, label: 'Wait for response', at: 'seconds of silence before re-asking' },
            { n: 2, label: 'Ask cap', at: '2 = ask, then once more' },
            { n: 3, label: 'If the reason can’t be worked out', at: 'the intent unmatched calls go to' },
            { n: 4, label: 'Confidence threshold', at: 'how sure the AI must be' },
            { n: 5, label: 'Re-ask line', at: 'per language; answer is read as the caller type' },
          ],
        },
        goodToKnow: ['Ask cap 2 = the caller is asked twice in total.'],
        who: whoSettings('Workflow'),
        keywords: ['classify', 'caller types', 'keywords', 'routing', 'keypad menu', 'confidence', 'ask cap'],
      },
      {
        id: 'wf-intents',
        title: 'Intents',
        group: 'Workflow',
        menuPath: 'Settings › Workflow › an intent card',
        appLink: '/configuration/workflow',
        appLabel: 'Workflow',
        what: 'An intent is one reason for calling inside a caller type - Prescription, Appointment, Referral.',
        steps: [
          '+ Add intent under a caller type.',
          'Set its name and request types (e.g. Schedule, Change, Cancel). Its keywords are in Classify › Keyword library.',
          'The intent is the category its calls get - the chip in Messages (e.g. Prescription).',
          'Open the intent’s cards from the canvas.',
        ],
        screenshot: {
          file: img('wf-intents'),
          capture: 'The Patient caller type and its intents on the canvas.',
          markers: [
            { n: 1, label: 'Caller type’s intents', at: 'Patient - pick one' },
            { n: 2, label: 'An intent', at: 'Appointment = the category on its messages' },
            { n: 3, label: 'Request types', at: 'Schedule, Change, Cancel' },
            { n: 4, label: '+ Add intent', at: 'new reason for calling' },
            { n: 5, label: 'Nodes', at: 'the cards inside the chosen intent' },
          ],
        },
        goodToKnow: [
          'Renaming the intent renames the category on old and new messages.',
          'A call that takes no intent shows “Unclassified”.',
        ],
        who: whoSettings('Workflow'),
        keywords: ['intent', 'category', 'add intent', 'keywords', 'rx refill'],
      },
      {
        id: 'wf-cards',
        title: 'Cards inside an intent',
        group: 'Workflow',
        menuPath: 'Settings › Workflow › an intent › its cards',
        appLink: '/configuration/workflow',
        appLabel: 'Workflow',
        what: 'The steps that do the work of an intent.',
        steps: [
          'Pick a card’s type, set its Ask cap and routing, then its wording per language.',
          '+ Add step adds a question; Stores says which field it saves.',
          'Question budget: the most questions the card may ask in total.',
          'Ask together: one combined question for all its steps (e.g. name and date of birth). Fields read from the answer shows what is saved.',
        ],
        fields: [
          { name: 'Identity', text: 'Asks who the caller is - name, date of birth. Each step stores a field.' },
          { name: 'Request', text: 'Splits into request types (Make, Change, Cancel), each with its own steps.' },
          { name: 'Connect', text: 'Calls a connected system action (Integrations). Says a success or failure line, then routes.' },
          { name: 'Confirm', text: 'Reads the details back. Yes goes on; a correction is applied; can’t be confirmed goes where you choose.' },
          { name: 'TextConfirm', text: 'Asks if the caller wants a text; sends only on a clear yes, only to a mobile.' },
          { name: 'Announce', text: 'Says one line, asks nothing (e.g. the reference number).' },
        ],
        screenshot: {
          file: img('wf-cards'),
          capture: 'The Identity card drawer.',
          markers: [
            { n: 1, label: 'Card type', at: 'Identity, Request, Connect, Confirm, TextConfirm, Announce' },
            { n: 2, label: 'Ask cap', at: 'tries per question' },
            { n: 3, label: 'Question budget', at: 'max questions this card may ask' },
            { n: 4, label: 'Ask together', at: 'one combined question for all steps' },
            { n: 5, label: 'Combined question', at: 'per language' },
            { n: 6, label: 'Fields read from the answer', at: 'what is stored' },
          ],
        },
        goodToKnow: [
          'A low-confidence capture is flagged for review, not asked again - it shows as FLAGGED in Messages.',
          'Shared cards (ALL) run in every intent of the caller type. A Connect card shows DISABLED until its system is set up in Integrations.',
          'Something else is always the last intent of a caller type and can’t be deleted.',
        ],
        who: whoSettings('Workflow'),
        keywords: ['identity', 'request', 'connect', 'confirm', 'text confirm', 'announce', 'steps', 'stores', 'ask cap'],
      },
      {
        id: 'wf-interceptor',
        title: 'Mid-call help (Interceptor)',
        group: 'Workflow',
        menuPath: 'Settings › Workflow › Interceptor',
        appLink: '/configuration/workflow',
        appLabel: 'Workflow',
        what: 'Listens on every turn and acts before the current question takes the words as an answer.',
        steps: [
          'Call intent verification: changes course when the caller’s words don’t fit what was asked.',
          'Say that again: repeats the agent’s last line when the caller didn’t catch it.',
          'Transfer or end call: words that take the caller to staff, voicemail or the end of the call - from anywhere.',
          'Off-script question: answers from the Knowledge Base, then picks the call back up.',
          'Go back & start over: steps the caller back one question, or restarts the request.',
          'Change of intent: not available yet.',
          'Each block has its own switch and fires on its own situation - there is no order to set.',
        ],
        screenshot: {
          file: img('wf-interceptor'),
          capture: 'Interceptor drawer, English tab.',
          markers: [
            { n: 1, label: 'Call intent verification', at: 'NEW - changes course when the words don’t fit' },
            { n: 2, label: 'Say that again', at: 'repeats the last line' },
            { n: 3, label: 'Transfer or end call', at: 'words that leave the flow' },
            { n: 4, label: 'Off-script question', at: 'answers from the Knowledge Base, then resumes' },
            { n: 5, label: 'On/off per block', at: 'each fires on its own situation' },
          ],
        },
        goodToKnow: ['A phrase inside a real answer does not trigger - “sorry, it’s Tylenol” still captures Tylenol.'],
        who: whoSettings('Workflow'),
        keywords: ['interceptor', 'say that again', 'repeat', 'transfer', 'end call', 'off-script', 'questions', 'go back'],
      },
      {
        id: 'wf-endings',
        title: 'How a call ends',
        group: 'Workflow',
        menuPath: 'Settings › Workflow › terminal cards',
        appLink: '/configuration/workflow',
        appLabel: 'Workflow',
        what: 'Every call ends at a terminal card.',
        steps: [
          'Transfer to staff: Action Handoff rings your Handoff line (Who answers shows it, from AI Agent › Handoff), or Take a message.',
          'Route to operator: your Outside operator line, at any hour, once Verified.',
          'Leave a Message (voicemail or take a message) · End call.',
          'Thank You (Success) and Thank You (Failure) - always there; a disabled terminal falls back to Thank You (Failure).',
          'Each has its closing Message and Keywords per language.',
        ],
        screenshot: {
          file: img('wf-endings'),
          capture: 'Transfer to staff card drawer.',
          markers: [
            { n: 1, label: 'Key', at: 'handoff - fixed' },
            { n: 2, label: 'Action', at: 'Handoff or Take a message' },
            { n: 3, label: 'Who answers', at: 'read-only summary from AI Agent › Handoff' },
            { n: 4, label: 'Edit in AI Agent › Handoff', at: 'link' },
            { n: 5, label: 'Keywords', at: 'words that jump here from anywhere, per language' },
          ],
        },
        goodToKnow: [
          'Route to operator is greyed while the Outside operator line is off.',
          'Keywords on a terminal take the caller there from anywhere in the call.',
        ],
        who: whoSettings('Workflow'),
        keywords: ['terminal', 'transfer to staff', 'voicemail', 'end call', 'operator', 'thank you', 'ending'],
      },
      {
        id: 'wf-phrases',
        title: 'Agent phrases',
        group: 'Workflow',
        menuPath: 'Settings › Workflow › Phrases',
        appLink: '/configuration/workflow',
        appLabel: 'Workflow',
        what: 'Every standard line the agent speaks - re-asks, “anything else?”, goodbyes - in your own words, per language.',
        steps: ['Open the Phrases card.', 'Pick a language tab.', 'Rewrite a line. Leave it blank to keep the platform line.', 'Save.'],
        screenshot: {
          file: img('wf-phrases'),
          capture: 'Phrases drawer, English tab.',
          markers: [
            { n: 1, label: 'Language tabs', at: 'EN / ES' },
            { n: 2, label: 'Section', at: 'Understanding the call, confirm, wrap-up…' },
            { n: 3, label: 'A phrase', at: 'After answer' },
            { n: 4, label: 'Template default', at: 'blank = platform line' },
            { n: 5, label: 'Preview / Play', at: 'hear it' },
          ],
        },
        goodToKnow: [
          'A blank line always falls back to the platform wording.',
          'Read-backs (next to Phrases): a detail is read back when its confidence is under the step’s Confidence.',
        ],
        who: whoSettings('Workflow'),
        keywords: ['phrases', 'agent phrases', 'wording', 'goodbye', 'anything else', 're-ask'],
      },
      {
        id: 'wf-saving',
        title: 'Saving your workflow',
        group: 'Workflow',
        menuPath: 'Settings › Workflow › Save',
        appLink: '/configuration/workflow',
        appLabel: 'Workflow',
        what: 'Saving checks the workflow and registers any new fields.',
        steps: [
          'Click Save.',
          '“Register new fields?” - a step stores a field we haven’t seen. Give each a label and a type (Text, Number, Date, Phone).',
          'Click Register and save.',
          'A failed save says “Could not save” and why; the drawer stays open.',
        ],
        screenshot: {
          file: img('wf-saving'),
          capture: 'The “Register a new field?” dialog with one field.',
          markers: [
            { n: 1, label: 'The new field', at: 'field row' },
            { n: 2, label: 'Type', at: 'type picker' },
            { n: 3, label: 'Register and save', at: 'button' },
          ],
        },
        goodToKnow: ['A type lets the agent check what it heard - days of supply should be a number.'],
        who: whoSettings('Workflow'),
        keywords: ['save workflow', 'register new fields', 'could not save', 'publish'],
      },
    ],
  },

  // ── 7 · Common questions and fixes ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'common-questions',
    title: 'Common questions',
    reader: 'front desk',
    rail: 'start',
    intro: 'Quick fixes for the questions we hear most.',
    articles: [
      {
        id: 'faq-no-code',
        title: 'I didn’t get the code or the reset email',
        menuPath: 'The sign-in page',
        what: 'The code or link did not arrive.',
        steps: [
          'Look at what the page says it sent to. Is the email or mobile typed right?',
          'Check spam or junk.',
          'Wait 30 seconds, then Resend code.',
          '“Technical error while sending the code. Please try another method.” - use the other button or your password.',
          'Still nothing? Ask your administrator to check your email and mobile in Users.',
        ],
        screenshot: {
          file: img('faq-no-code'),
          capture: '“Check your email” after Email me a code with a mistyped test address.',
          markers: [{ n: 1, label: 'What you typed', at: 'bold address in the sentence' }],
        },
        goodToKnow: ['The page always says “If an account matches…” - for safety it never says whether the account exists.'],
        who: WHO_ANYONE,
        keywords: ['no code', 'code not received', 'reset email', 'didn’t get', 'spam', 'resend'],
      },
      {
        id: 'faq-password-refused',
        title: 'My password is refused',
        menuPath: 'Set up your login · Choose a new password',
        what: 'A rule under the password box is not green.',
        steps: [
          'Read the red rule.',
          'Too short: make it longer - a phrase is easiest.',
          'Has your name or email: take it out.',
          '“Not common, easy to guess or the practice’s name or short name”: choose something less common, without the practice’s name.',
          'One of your last passwords: choose a new one.',
        ],
        screenshot: {
          file: img('faq-password-refused'),
          capture: 'New-password screen with “harlem2024” typed (the common rule red).',
          markers: [{ n: 1, label: 'The red rule', at: 'rule list' }],
        },
        goodToKnow: ['“This password is too common, too easy to guess, or uses the practice’s name or short name. Choose a different one.”'],
        who: WHO_ANYONE,
        keywords: ['password refused', 'password rejected', 'too common', 'weak password', 'password rules'],
      },
      {
        id: 'faq-no-recording',
        title: 'A call has no recording',
        menuPath: 'Messages › open a call › Recording',
        appLink: '/messages',
        appLabel: 'Messages',
        what: 'The message says “No recording was saved for this call.”',
        steps: [
          '“…the caller hung up before leaving a message” - it was a missed call; nothing to play.',
          'Recording may be off - Settings › Call Handling › Recording › Record call audio.',
          'Old calls: recordings are removed after the Recording retention time.',
          'Read the transcript instead, if it was kept.',
        ],
        screenshot: {
          file: img('faq-no-recording'),
          capture: 'A missed test call showing the no-recording note.',
          markers: [
            { n: 1, label: 'The no-recording note', at: 'why there is no audio' },
            { n: 2, label: 'Hung up', at: 'how the call ended' },
            { n: 3, label: 'Unclassified', at: 'no request heard' },
          ],
        },
        goodToKnow: [],
        who: WHO_MESSAGES,
        keywords: ['no recording', 'missing recording', 'cannot play', 'recording not saved'],
        alsoIn: ['messages'],
      },
      {
        id: 'faq-general',
        title: 'Why is this call “General”?',
        menuPath: 'Messages › the category chip',
        appLink: '/messages',
        appLabel: 'Messages',
        what: 'Unclassified (or General) means the call did not take an intent, so it has no other category.',
        steps: [
          'The caller hung up early, or asked for something no intent covers.',
          'Listen to the call or read the summary.',
          'If calls like it are common, ask your admin to add keywords or an intent for them.',
        ],
        screenshot: {
          file: img('faq-general'),
          capture: 'A test call with the Unclassified chip.',
          markers: [
            { n: 1, label: 'Unclassified chip', at: 'no request heard / no intent taken' },
          ],
        },
        goodToKnow: [],
        who: WHO_MESSAGES,
        keywords: ['general', 'category', 'uncategorized', 'wrong category'],
        alsoIn: ['messages'],
      },
      {
        id: 'faq-wrong-language',
        title: 'The caller heard English instead of Spanish',
        menuPath: 'Settings › AI Agent › Agent Details · Workflow › Language detect',
        appLink: '/configuration/workflow',
        appLabel: 'Workflow',
        what: 'The agent starts in your primary language and switches when it hears another.',
        steps: [
          'Check Spanish is added in Settings › AI Agent › Agent Details.',
          'In Workflow › Language detect, turn on Announce so Spanish callers hear they can say “Español”.',
          'Check the confidence there is not too high.',
          'Listen to the call - a very short answer may not be enough to switch.',
        ],
        screenshot: {
          file: img('faq-wrong-language'),
          capture: 'Language detect card, Spanish tab, Announce on.',
          markers: [{ n: 1, label: 'Announce', at: 'switch' }],
        },
        goodToKnow: ['Read in on the message shows the summary in another language - it does not change the call.'],
        who: whoSettings('Workflow'),
        keywords: ['spanish', 'language', 'wrong language', 'english', 'language detect', 'announce'],
        alsoIn: ['settings'],
      },
      {
        id: 'faq-keypad',
        title: 'The caller pressed a key and nothing happened',
        menuPath: 'Messages › open a call › Call path · Settings › Call Handling › Keypad menus',
        appLink: '/messages',
        appLabel: 'Messages',
        what: 'A caller says they pressed a key during the menu, but the call did not move on.',
        steps: [
          'The menu is say-or-press: saying the answer works too, so the caller can just say it.',
          'Open the call and switch on Call path - it shows the menu and what was heard or pressed.',
          'If a key press is missing, send the reference number (e.g. #41-0014) to VoiceSpectrum Support.',
        ],
        screenshot: {
          file: img('set-ch-keypad'),
          capture: 'Keypad menus section, English tab.',
          markers: [
            { n: 3, label: 'Repeat this menu', at: 'key 0' },
            { n: 4, label: 'Back to the main menu', at: 'key *' },
            { n: 5, label: 'Speak with someone', at: 'key 9' },
          ],
        },
        goodToKnow: ['Keys and their wording are in Settings › Call Handling › Keypad menus.'],
        who: WHO_ANYONE,
        keywords: ['keypad', 'pressed', 'press', 'key', 'button', 'dtmf', 'menu', 'nothing happened', 'digits'],
        alsoIn: ['messages'],
      },
      {
        id: 'faq-texts',
        title: 'Texts aren’t arriving',
        menuPath: 'Messages › Send Text · Settings › Communication › SMS testing',
        appLink: '/configuration/communication',
        appLabel: 'Communication',
        what: 'A text from Messages or the workflow did not reach the caller.',
        steps: [
          'Check the number. Texts go to mobiles only.',
          'Check the caller did not decline texts (Contacts › Text consent).',
          'Send a test text in Settings › Communication › SMS testing.',
          'Still failing? Contact support.',
        ],
        screenshot: {
          file: img('faq-texts'),
          capture: 'SMS testing with a result shown.',
          markers: [
            { n: 1, label: 'Send a test text to', at: 'number box and Send test' },
            { n: 2, label: 'Text messaging is enabled', at: 'your practice number' },
          ],
        },
        goodToKnow: [
          'The workflow’s text card skips landlines and office lines in silence.',
          'A caller who replied STOP gets no more texts until they reply START.',
        ],
        who: WHO_ANYONE,
        keywords: ['texts', 'sms', 'not arriving', 'text failed', 'consent'],
        alsoIn: ['messages'],
      },
      {
        id: 'faq-first-words',
        title: 'How do I change what the agent says first?',
        menuPath: 'Settings › Workflow › Greeting',
        appLink: '/configuration/workflow',
        appLabel: 'Workflow',
        what: 'The first words are the Greeting card (after any Disclosure).',
        steps: ['Open Settings › Workflow.', 'Click the Greeting card.', 'Change the text on each language tab.', 'Save.'],
        screenshot: {
          file: img('faq-first-words'),
          capture: 'Greeting card drawer.',
          markers: [
            { n: 1, label: 'Greeting text', at: 'what the caller hears first' },
            { n: 2, label: 'Regular', at: 'every call - ID matched and Repeat caller below' },
          ],
        },
        goodToKnow: ['The recording notice before it is the Disclosure card.'],
        who: whoSettings('Workflow'),
        keywords: ['greeting', 'first words', 'welcome message', 'change greeting', 'what the agent says'],
        alsoIn: ['settings'],
      },
      {
        id: 'faq-stop-recording',
        title: 'How do I stop recording?',
        menuPath: 'Settings › Call Handling › Recording',
        appLink: '/configuration/call-handling',
        appLabel: 'Call Handling',
        what: 'Switch off Record call audio.',
        steps: [
          'Open Settings › Call Handling › Recording.',
          'Switch Record call audio off. Save.',
          'Also update the Disclosure card in Workflow so callers are not told the call is recorded.',
        ],
        screenshot: {
          file: img('faq-stop-recording'),
          capture: 'Recording section with Record call audio off.',
          markers: [{ n: 1, label: 'Record call audio', at: 'switch' }],
        },
        goodToKnow: ['The transcript is separate - Keep call transcript.'],
        who: whoSettings('Call Handling'),
        keywords: ['stop recording', 'turn off recording', 'recording off', 'disclosure'],
        alsoIn: ['settings'],
      },
      {
        id: 'faq-who-sees',
        title: 'Who can see which messages?',
        menuPath: 'Settings › Security › User groups',
        appLink: '/configuration/security',
        appLabel: 'Security',
        what: 'User groups decide who can open Messages and what they can do there.',
        steps: [
          'Open Settings › Security › User groups.',
          'Find the Messages rows. None hides Messages; View reads; Work acts; Full also deletes.',
          'Today everyone with access sees every message - “Assigned to me” only is coming.',
        ],
        screenshot: {
          file: img('faq-who-sees'),
          capture: 'User groups grid filtered to the messages rows.',
          markers: [
            { n: 1, label: 'Messages rows', at: 'Phone calls and Text messages' },
            { n: 2, label: 'The level', at: 'View is enough to read' },
          ],
        },
        goodToKnow: [],
        who: whoSettings('Security'),
        keywords: ['who can see', 'permissions', 'access', 'privacy', 'messages access'],
        alsoIn: ['messages'],
      },
    ],
  },
];

// v12.10.23.1 (#2102, Tushar 10/29): Help follows the person's rights - an article shows only when the person can open
// (any action, View is enough) at least one of the access areas listed here; ids not listed are for everyone (Getting
// started, Signing in). Area keys are the ones the left menu and Settings use (useAccess): 'phone' / 'texts' (Messages,
// Follow-ups), 'contacts', 's:<Area>:<Section>', 'sec_platform' / 'sec_users' / 'sec_roles' / 'sec_groups';
// '*settings' = any Settings or Security area. The server still guards every screen - this only hides topics.
const MSG = ['phone', 'texts'];
const SA = (area: string, ...sections: string[]) => sections.map((x) => `s:${area}:${x}`);
export const HELP_ACCESS: Record<string, string[]> = {
  'gs-glossary': [...MSG, 'contacts', '*settings'],
  'msg-list': MSG, 'msg-search': MSG, 'msg-read': MSG, 'msg-listen': MSG, 'msg-route': MSG, 'msg-callback': MSG,
  'msg-followups': MSG, 'msg-notes': MSG, 'msg-assign': MSG, 'msg-shortcuts': MSG,
  'ct-find-add': ['contacts'], 'ct-import': ['contacts'], 'ct-page': ['contacts'], 'ct-linked': ['contacts'], 'ct-merge': ['contacts'], 'ct-delete': ['contacts'],
  'set-home': ['*settings'],
  'set-org-identity': SA('Organization', 'Identity'), 'set-org-hours': SA('Organization', 'Hours & locale'), 'set-org-holidays': SA('Organization', 'Holiday dates'),
  'set-ai-phone': SA('AI Agent', 'Phone & activation'), 'set-ai-details': SA('AI Agent', 'Agent details'),
  'set-ai-voicemail': SA('AI Agent', 'Voicemail'), 'set-ai-handoff': SA('AI Agent', 'Handoff'),
  'set-ch-flow': SA('Call Handling', 'Conversation flow'), 'set-ch-keypad': SA('Call Handling', 'Conversation flow'),
  'set-ch-processing': SA('Call Handling', 'Processing behavior', 'Hold behavior'), 'set-ch-recording': SA('Call Handling', 'Recording'),
  'set-rel-provider': SA('Reliability', 'Provider & processing', 'Platform connectivity'), 'set-rel-failure-msg': SA('Reliability', 'Failure message'),
  'set-kb-retrieval': SA('Knowledge Base', 'Retrieval'), 'set-kb-documents': SA('Knowledge Base', 'Documents'), 'set-kb-quick': SA('Knowledge Base', 'Quick answers'),
  'set-sec-platform': ['sec_platform'], 'set-sec-support': ['sec_platform'], 'set-sec-users': ['sec_users'], 'set-sec-groups': ['sec_roles', 'sec_groups'],
  'set-comm-email': SA('Communication', 'Email (SMTP)'), 'set-comm-sms': SA('Communication', 'SMS testing'),
  'set-pz-callertypes': SA('Personalization', 'Workspace'), 'set-pz-categories': SA('Personalization', 'Workspace'),
  'set-pz-labels': SA('Personalization', 'Workspace'), 'set-pz-grouping': SA('Personalization', 'Workspace'), 'set-pz-workspace': SA('Personalization', 'Workspace'),
  'set-voc-fields': SA('Vocabulary', 'Fields'), 'set-voc-lists': SA('Vocabulary', 'Lists'),
  'set-int-webhooks': SA('Integrations', 'Webhooks'), 'set-int-connected': SA('Integrations', 'Connected systems'),
  'set-int-actions': SA('Integrations', 'Connected systems'), 'set-int-failure': SA('Integrations', 'Failure policy'),
  'wf-overview': SA('Workflow', 'Workflow'), 'wf-before': SA('Workflow', 'Workflow'), 'wf-classify': SA('Workflow', 'Workflow'),
  'wf-intents': SA('Workflow', 'Workflow'), 'wf-cards': SA('Workflow', 'Workflow'), 'wf-interceptor': SA('Workflow', 'Workflow'),
  'wf-endings': SA('Workflow', 'Workflow'), 'wf-phrases': SA('Workflow', 'Workflow'), 'wf-saving': SA('Workflow', 'Workflow'),
  'faq-no-recording': MSG, 'faq-general': MSG, 'faq-wrong-language': MSG, 'faq-texts': MSG, 'faq-first-words': MSG, 'faq-keypad': MSG,
  'faq-stop-recording': [...MSG, ...SA('Call Handling', 'Recording')], 'faq-who-sees': ['sec_roles', 'sec_groups', 'sec_users'],
};
/** Answers one area key for the person ('*settings' = any Settings / Security area). */
export type HelpCanOpen = (areaKey: string) => boolean;
/** Can this person see the article? No canOpen (or an article for everyone) -> yes. */
export function helpAllowed(articleId: string, canOpen?: HelpCanOpen): boolean {
  const keys = HELP_ACCESS[articleId];
  if (!canOpen || !keys || !keys.length) return true;
  return keys.some((k) => canOpen(k));
}
/** Does this Help menu item have at least one topic the person may see? */
export function helpRailAllowed(rail: HelpRail, canOpen?: HelpCanOpen): boolean {
  return helpTopicsFor(rail, canOpen).some((g) => g.entries.length > 0);
}

/** Every article with its section, in library order. */
export interface HelpEntry {
  section: HelpSection;
  article: HelpArticle;
}
export const HELP_ENTRIES: HelpEntry[] = HELP_SECTIONS.flatMap((section) => section.articles.map((article) => ({ section, article })));

export function findHelpEntry(id: string | null | undefined): HelpEntry | undefined {
  return id ? HELP_ENTRIES.find((e) => e.article.id === id) : undefined;
}

/** The topic groups shown for one left-menu item: { heading, entries } in order. */
export function helpTopicsFor(rail: HelpRail, canOpen?: HelpCanOpen): { heading: string; entries: HelpEntry[] }[] {
  const groups: { heading: string; entries: HelpEntry[] }[] = [];
  for (const e of HELP_ENTRIES) {
    if (!helpAllowed(e.article.id, canOpen)) continue;   // v12.10.23.1 (#2102)
    const home = e.section.rail === rail;
    const also = (e.article.alsoIn ?? []).includes(rail);
    if (!home && !also) continue;
    const heading = home ? (e.article.group ?? e.section.title)
      : e.section.id === 'common-questions' ? e.section.title : (HELP_RAILS.find((r) => r.key === rail)?.label ?? e.section.title);
    let g = groups.find((x) => x.heading === heading);
    if (!g) { g = { heading, entries: [] }; groups.push(g); }
    g.entries.push(e);
  }
  // Common questions always last.
  return [...groups.filter((g) => g.heading !== 'Common questions'), ...groups.filter((g) => g.heading === 'Common questions')];
}

/** Client-side search over title, what, steps, field names, keywords, menu path and good-to-know. */
export function searchHelp(query: string, canOpen?: HelpCanOpen): HelpEntry[] {
  const words = query.toLowerCase().split(/\s+/).map((w) => w.trim()).filter(Boolean);
  if (!words.length) return [];
  const scored: { e: HelpEntry; score: number }[] = [];
  for (const e of HELP_ENTRIES) {
    if (!helpAllowed(e.article.id, canOpen)) continue;   // v12.10.23.1 (#2102)
    const a = e.article;
    const title = a.title.toLowerCase();
    const keys = a.keywords.join(' ').toLowerCase();
    const fields = (a.fields ?? []).map((f) => `${f.name} ${f.text}`).join(' ').toLowerCase();
    const body = [a.what, a.menuPath, ...a.steps, ...a.goodToKnow, a.group ?? '', e.section.title].join(' ').toLowerCase();
    let score = 0;
    let all = true;
    for (const w of words) {
      const t = title.includes(w), k = keys.includes(w), f = fields.includes(w), b = body.includes(w);
      if (!t && !k && !f && !b) { all = false; break; }
      score += (t ? 8 : 0) + (k ? 4 : 0) + (f ? 2 : 0) + (b ? 1 : 0);
    }
    if (all) scored.push({ e, score });
  }
  return scored.sort((x, y) => y.score - x.score).map((x) => x.e);
}
